import React, { Fragment, useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from 'primereact/fileupload';
import { RadioButton } from 'primereact/radiobutton';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { AutoComplete } from 'primereact/autocomplete';
import { ToastService } from '@iamsoftware/react-hooks';
import { CommonService } from '../../../service/CommonService';
import { CustomerService } from '../../danh-muc/khach-hang/CustomerService';
import { useCustomerDialog } from '../../../hooks/useCustomerDialog';
import { Oauth2GoogleService } from '../../hop-dong/mau-hop-dong/Oauth2GoogleService';
import { GoogleService } from '../GoogleService';
import { Menu } from 'primereact/menu';
import { ScrollPanel } from 'primereact/scrollpanel';
import Draggable from 'react-draggable';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { AgreementService } from 'src/service/AgreementService';
import { PdfCanvasViewer } from '../PdfCanvasViewer';
import { AgreementConfigService } from 'src/service/AgreementConfigService';
import { AgreementTemplateService } from '../../quan-ly-loai-tai-lieu/tai-lieu-mau/AgreementTemplateService';
import { Step2Signers } from './components/Step2Signers';
import { Step3Positions } from './components/Step3Positions';

GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

// Helper function to extract file extension from fileName and contentType
const extractFileExtension = (fileName?: string, contentType?: string): string => {
  // Try to extract extension from fileName
  if (fileName && typeof fileName === 'string') {
    const parts = fileName.split('.');
    if (parts.length > 1) {
      const ext = parts[parts.length - 1].toLowerCase();
      // Only use extension if it's not a number (like "123")
      if (ext && !/^\d+$/.test(ext)) {
        return ext;
      }
    }
  }

  // If no valid extension from fileName, try to get from contentType
  if (contentType) {
    if (contentType.includes('wordprocessingml')) return 'docx';
    if (contentType.includes('pdf')) return 'pdf';
    if (contentType.includes('spreadsheetml')) return 'xlsx';
    if (contentType.includes('presentationml')) return 'pptx';
    if (contentType.includes('msword')) return 'doc';
    if (contentType.includes('excel')) return 'xls';
    if (contentType.includes('powerpoint')) return 'ppt';
  }

  return 'docx';
};

type FormTypeOption = {
  label: string;
  value: string;
  enumCode?: string | null;
  raw?: any;
};

export const TaiLieu: React.FC = () => {
  const navigate = useNavigate();
  const { agreementId } = useParams();
  const [searchParams] = useSearchParams();
  const fileUploadRef = useRef<FileUpload>(null);
  const effectiveDateCalendarRef = useRef<any>(null);
  const expiryDateCalendarRef = useRef<any>(null);
  const [initializing, setInitializing] = useState<boolean>(false);
  const [isViewMode, setIsViewMode] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    agreementName: '',
    agreementType: null,
    effectiveDate: null,
    expiryDate: null,
    agreementNo: '',
    agreementNoInt: null as number | null,
    agreementNoFormatEnumId: null as string | null,
    description: '',
    isImportant: false,
    isUrgent: false
  });

  const [attachedFiles, setAttachedFiles] = useState<any[]>([]);
  const [mainFiles, setMainFiles] = useState<any[]>([]);
  const [isValid, setIsValid] = useState(false);
  const [isFileOptionsExpanded, setIsFileOptionsExpanded] = useState(false);
  const [isAttachedFilesExpanded, setIsAttachedFilesExpanded] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [googleFileId, setGoogleFileId] = useState<string>('');
  const [showGDoc, setShowGDoc] = useState<boolean>(false);
  const [displayEditor, setDisplayEditor] = useState<any>(null);
  const [pseudoIdFormat, setPseudoIdFormat] = useState([])
  const [documentRecipients, setDocumentRecipients] = useState<any[]>([{
    _internalId: crypto.randomUUID(),
    party: null,
    taxCode: '',
    partyName: '',
    emailAddress: '',
    contactNumber: '',
    representativeName: '',
    identityCard: '',
    roleTypeId: '',
    signingOrder: 1
  }]);
  const [hasSigner, setHasSigner] = useState<boolean>(false);
  const [signerRoleIds, setSignerRoleIds] = useState<any[]>([]);
  const [roleOptions, setRoleOptions] = useState<any[]>([]);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [detailAgreementTypeOption, setDetailAgreementTypeOption] = useState<FormTypeOption | null>(null);
  const [documentTypeItems, setDocumentTypeItems] = useState<any[]>([]);
  const [documentTypeItemsLoading, setDocumentTypeItemsLoading] = useState<boolean>(false);
  const [documentTypeItemMeta, setDocumentTypeItemMeta] = useState<Record<string, { formType?: string | null; receivedDate?: Date | null }>>({});
  const [fileTypes, setFileTypes] = useState<any[]>([]);
  const [showRecipients, setShowRecipients] = useState<boolean>(false);
  const [showSignaturePlacement, setShowSignaturePlacement] = useState<boolean>(false);
  const [selectedSigner, setSelectedSigner] = useState<any>(null);
  const [signaturePositions, setSignaturePositions] = useState<any[]>([]);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageCanvases, setPageCanvases] = useState<any[]>([]);
  const [canvasLoading, setCanvasLoading] = useState<boolean>(false);
  const [selectedSignatureType, setSelectedSignatureType] = useState<string>('AgtDigitalVerified');
  const canvasRefs = useRef<any[]>([]);
  const draggableRefs = useRef({} as any);
  const [filePreviews, setFilePreviews] = useState<{ [key: string]: string }>({});
  const [previewLoading, setPreviewLoading] = useState<{ [key: string]: boolean }>({});
  const [showFileViewer, setShowFileViewer] = useState<boolean>(false);
  const [fileOperationLoading, setFileOperationLoading] = useState<boolean>(false);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState<Set<string>>(new Set());
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [pdfScale, setPdfScale] = useState<number>(1.0);
  const [pdfPages, setPdfPages] = useState<any[]>([]);
  const [pendingHash, setPendingHash] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const markDirty = () => setHasUnsavedChanges(true);
  const [showLeavePrompt, setShowLeavePrompt] = useState<boolean>(false);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [isTemplateMode, setIsTemplateMode] = useState<boolean>(false);
  const [isSequential, setIsSequential] = useState<boolean>(false);
  const [templateAgreementId, setTemplateAgreementId] = useState<string | null>(null);
  const isFromTemplateAgreement = useMemo(() => isTemplateMode && !!templateAgreementId, [isTemplateMode, templateAgreementId]);
  const [activeRecipientIndex, setActiveRecipientIndex] = useState<number | null>(null);
  const toast = useRef<Toast>(null);
  const [activeMainFileIndex, setActiveMainFileIndex] = useState<number>(0);
  const [party, setParty] = useState<any>(null);
  const formatSeq = (n: number) => (n < 10 ? `0${n}` : String(n));
  const normalizeLocationSeq = (positions: any[]) =>
    positions.map((pos, idx) => ({
      ...pos,
      locationSeqId: formatSeq(idx + 1)
    }));
  const mapAgreementTypes = (payload: any): any[] => {
    const source = Array.isArray(payload)
      ? payload
      : payload?.list ?? payload?.listData ?? [];
    if (!Array.isArray(source)) return [];
    return source
      .map((item: any) => {
        if (item?.label && item?.value) {
          return item;
        }
        const value =
          item?.agreementConfigureEnumId ||
          item?.agreementTypeId ||
          item?.value ||
          '';
        const label =
          item?.agreementConfigureName ||
          item?.agreementTypeName ||
          item?.description ||
          item?.label ||
          value;
        return {
          ...item,
          value,
          label
        };
      })
      .filter((item: any) => item?.value);
  };
  const resolveConfigItemSeq = (item: any, fallbackIndex?: number): number | null => {
    if (!item) return fallbackIndex !== undefined ? fallbackIndex + 1 : null;
    const seq =
      item.sequenceNum ??
      item.configSequenceNum ??
      item.agreementItemSeq ??
      item.agreementConfigureItemSeq ??
      item.id ??
      item.value ??
      (fallbackIndex !== undefined ? fallbackIndex + 1 : null);
    const parsed = Number(seq);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const renderPage = (pdf: any, pageNumber: number) => {
    const totalPages = Number(pdf?.numPages) || 1;
    const requested = Number(pageNumber);
    const safePage = Math.min(Math.max(1, Number.isFinite(requested) ? requested : 1), totalPages);
    return pdf.getPage(safePage).then((page: any) => {
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      return page.render(renderContext).promise.then(() => canvas);
    });
  };

  const formTypeOptions = useMemo<FormTypeOption[]>(() => {
    if (Array.isArray(fileTypes) && fileTypes.length > 0) {
      return fileTypes.map((ft: any) => ({
        label: String(ft.description || ft.enumName || ft.enumId || ''),
        value: String(ft.enumId || ft.value || ft.enumCode || ''),
        enumCode: ft.enumCode ? String(ft.enumCode) : String(ft.enumId || ''),
        raw: ft
      })) as FormTypeOption[];
    }
    return [] as FormTypeOption[];
  }, [fileTypes]);

  const normalizeFormTypeValue = (input?: string | null) => {
    if (!input) return null;
    const str = String(input);
    const option = formTypeOptions.find(
      (opt: any) => opt.value === str || opt.enumCode === str
    );
    if (option) return option.value;
    if (str === 'SOFT') return 'DIGITAL_COPY';
    if (str === 'HARD') return 'HARD_COPY';
    return str;
  };

  const loadPDFToCanvas = async (file: any) => {
    if (!file) return;

    setCanvasLoading(true);
    setPageCanvases([]);
    try {
      let arrayBuffer: ArrayBuffer;
      if (file.referenceFileId) {
        console.log('loadPDFToCanvas: file.referenceFileId =', file.referenceFileId);
        const blob = await AgreementService.downloadPdf(file.referenceFileId);
        arrayBuffer = await blob.arrayBuffer();
      } else if (file.googleFileId) {
        console.log('loadPDFToCanvas: file.googleFileId =', file.googleFileId);
        // Mở quyền truy cập file trước khi tải PDF
        if (!file.googleFileId.includes("/")) {
          await GoogleService.openAnyoneView(agreementId, file.googleFileId);
        }
        const blob = await AgreementService.downloadPdf(file.googleFileId);
        arrayBuffer = await blob.arrayBuffer();
      } else if (file.file) {
        arrayBuffer = await file.file.arrayBuffer();
      } else {
        throw new Error('No file source available');
      }

      const loadingTask = getDocument({ data: arrayBuffer });
      console.log('loadPDFToCanvas: arrayBuffer size =', arrayBuffer.byteLength);

      loadingTask.promise.then((pdf: any) => {
        console.log('loadPDFToCanvas: PDF loaded, numPages =', pdf.numPages);
        setNumPages(pdf.numPages);

        const canvasPromises = [];
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          canvasPromises.push(renderPage(pdf, pageNumber));
        }

        Promise.all(canvasPromises).then((canvases: any[]) => {
          console.log('loadPDFToCanvas: canvases rendered =', canvases.length);
          setPageCanvases(canvases);
          setCanvasLoading(false);
        });
      }, (reason: any) => {
        console.error('loadPDFToCanvas: PDF load error =', reason);
        setCanvasLoading(false);
      });
    } catch (error) {
      console.error('loadPDFToCanvas: Error =', error);
      setCanvasLoading(false);
    }
  };

  React.useEffect(() => {
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
  }, [hasUnsavedChanges]);

  React.useEffect(() => {
    let previousHash = window.location.hash;
    const onHashChange = () => {
      if (hasUnsavedChanges) {
        const target = window.location.hash;
        // revert ngay lập tức và mở dialog xác nhận
        window.location.hash = previousHash;
        setPendingHash(target);
        setShowLeavePrompt(true);
        return;
      }
      previousHash = window.location.hash;
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [hasUnsavedChanges]);

  const handleDrop = (event: any, pageIndex: number) => {
    const canvas = canvasRefs.current[pageIndex];
    const rect = canvas?.getBoundingClientRect();
    if (!canvas || !rect) return;

    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
    const canvasWidth = canvas.width || rect.width;
    const canvasHeight = canvas.height || rect.height;
    const scaleX = rect.width > 0 ? canvasWidth / rect.width : 1;
    const scaleY = rect.height > 0 ? canvasHeight / rect.height : 1;
    const cursorX = (event.clientX - rect.left) * scaleX;
    const cursorY = (event.clientY - rect.top) * scaleY;

    let defaultWidth = 200;
    let defaultHeight = (selectedSignatureType === 'AgtDigitalVerified') ? 65 : 100;
    
    // Đảm bảo các loại ký tay/ký ảnh luôn là 200x100
    if (selectedSignatureType !== 'AgtDigitalVerified') {
      defaultWidth = 200;
      defaultHeight = 100;
    }
    const xMax = canvasWidth - defaultWidth;
    const yMax = canvasHeight - defaultHeight;
    const x = clamp(cursorX, 0, Math.max(0, xMax));
    const y = clamp(cursorY, 0, Math.max(0, yMax));

    const resolvePartyId = () => {
      if (!selectedSigner) return null as any;
      if (typeof selectedSigner === 'string' || typeof selectedSigner === 'number') return selectedSigner as any;
      return (selectedSigner as any).partyId || (selectedSigner as any).value || null;
    };
    const getPartyLabel = (pid: any) => {
      const r = documentRecipients.find((d: any) => d.partyId === pid);
      const raw = r?.party?.label || r?.partyName || (selectedSigner && (selectedSigner as any).label) || 'Chưa chọn người ký';
      const m = String(raw).match(/\]\s*-\s*(.+)$/);
      return m ? m[1] : String(raw);
    };
    const pid = resolvePartyId();
    const displayLabel = getPartyLabel(pid);

    // Tìm thông tin party đầy đủ từ documentRecipients
    const recipient = documentRecipients.find((r: any) =>
      r.partyId === pid ||
      r.party?.partyId === pid ||
      r.party?.value === pid
    );
    const partyInfo = recipient?.party || {};

    // Tạo signature position với đầy đủ thông tin party
    setSignaturePositions(prev => {
      const newPosition: any = {
        locationSeqId: formatSeq(prev.length + 1),
        x,
        y,
        pageIndex,
        widthPx: defaultWidth,
        heightPx: defaultHeight,
        fontSize: 12,
        signatureTypeEnumId: selectedSignatureType,
        label: displayLabel,
        partyName: recipient?.partyName || partyInfo.partyName || displayLabel,
        partyId: pid,
        internalRecipientId: recipient?._internalId || null,
        roleTypeId: recipient?.roleTypeId || (selectedSigner && (selectedSigner as any).roleTypeId) || null,
        fileId: mainFiles[activeMainFileIndex]?.fileId || mainFiles[activeMainFileIndex]?.googleFileId || null,
        agreementContentId: mainFiles[activeMainFileIndex]?.agreementContentId || null
      };

      // Thêm toàn bộ thông tin party nếu có recipient
      if (recipient) {
        newPosition._internalId = recipient._internalId;
        newPosition.taxCode = recipient.taxCode || partyInfo.partyTaxId || partyInfo.taxCode || '';
        newPosition.emailAddress = recipient.emailAddress || partyInfo.emailAddress || '';
        newPosition.contactNumber = recipient.contactNumber || partyInfo.contactNumber || '';
        newPosition.representativeName = recipient.representativeName || partyInfo.representativeName || '';
        newPosition.identityCard = recipient.identityCard || partyInfo.idValue || partyInfo.identityCard || '';
        newPosition.party = {
          ...partyInfo,
          value: partyInfo.value || partyInfo.partyId || pid,
          partyId: partyInfo.partyId || pid,
          label: partyInfo.label || recipient.partyName || displayLabel
        };
      }

      return normalizeLocationSeq([...prev, newPosition]);
    });
    markDirty();
  };

  const handleDragStop = (event: any, data: any, locationSeqId: string) => {
    const updatedElements = signaturePositions.map((element) => {
      if (element.locationSeqId === locationSeqId) {
        const canvas = canvasRefs.current[element.pageIndex];
        if (!canvas) return element;

        const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
        const canvasWidth = canvas.width || 0;
        const canvasHeight = canvas.height || 0;
        const widthPx = Number(element.signatureTypeEnumId === 'AgtDigitalVerified' ? (element.widthPx || 200) : 200);
        const heightPx = Number(element.signatureTypeEnumId === 'AgtDigitalVerified' ? (element.heightPx || 65) : 100);
        const maxX = canvasWidth - widthPx;
        const maxY = canvasHeight - heightPx;
        const nx = clamp(data.x, 0, Math.max(0, maxX));
        const ny = clamp(data.y, 0, Math.max(0, maxY));


        return {
          ...element,
          x: nx,
          y: ny
        };
      }
      return element;
    });
    setSignaturePositions(normalizeLocationSeq(updatedElements));
    markDirty();
  };

  const removeSignaturePosition = (locationSeqId: string) => {
    setSignaturePositions(prev => normalizeLocationSeq(prev.filter(element => element.locationSeqId !== locationSeqId)));
    markDirty();
  };
  useEffect(() => {
    if (!selectedSigner) return;
    setSignaturePositions(prev => normalizeLocationSeq(prev.map(pos => {
      const updated = { ...pos } as any;
      if (!updated.partyId) updated.partyId = selectedSigner.partyId || selectedSigner.value || null;
      if (!updated.roleTypeId) updated.roleTypeId = selectedSigner.roleTypeId || null;
      if (!updated.partyName && selectedSigner.label) updated.partyName = selectedSigner.label;
      return updated;
    })));
  }, [selectedSigner]);

  // Sync signaturePositions with documentRecipients when recipient info changes
  // Also remove orphaned positions when their party is deleted
  useEffect(() => {
    if (!signaturePositions.length) return;

    // Get all valid partyIds from current recipients
    const validPartyIds = documentRecipients
      .map((r: any) => r.partyId || r.party?.partyId || r.party?.value)
      .filter(Boolean);

    setSignaturePositions(prev => {
      // Filter out orphaned positions 
      const filtered = prev.filter(pos => {
        // Kiểm tra xem hàng (recipient) này còn khớp với partyId cũ không
        const rec = documentRecipients.find(r => r._internalId === pos.internalRecipientId);
        if (rec) {
          const currentPartyId = rec.partyId || rec.party?.partyId || rec.party?.value;
          // Nếu đổi người ký trong cùng 1 hàng -> Xóa vị trí cũ (Ép kiểu String để so sánh chuẩn)
          if (pos.partyId && currentPartyId && String(pos.partyId) !== String(currentPartyId)) {
            return false;
          }
          // Nếu hàng đó bị xóa người ký (partyId === null) -> Xóa vị trí cũ
          if (pos.partyId && !currentPartyId) {
            return false;
          }
        }

        if (!pos.partyId) return true; // Keep positions without partyId
        const hasPartyMatch = documentRecipients.some(r => {
            const rPid = r.partyId || r.party?.partyId || r.party?.value;
            return rPid && String(rPid) === String(pos.partyId);
        });
        
        if (hasPartyMatch) return true;
        
        // If no party match, check if we can match by roleTypeId in template mode
        if (isTemplateMode || isFromTemplateAgreement) {
          return documentRecipients.some(r => r.roleTypeId === pos.roleTypeId);
        }
        return false;
      });

      // Update remaining positions with new recipient info
      const updated = filtered.map(pos => {
        const recipient = documentRecipients.find((r: any) =>
          (pos.internalRecipientId && r._internalId === pos.internalRecipientId) ||
          (pos.partyId && (String(r.partyId) === String(pos.partyId) || String(r.party?.partyId) === String(pos.partyId) || String(r.party?.value) === String(pos.partyId))) ||
          ((isTemplateMode || isFromTemplateAgreement) && r.roleTypeId === pos.roleTypeId)
        );
        if (!recipient) return pos;
        const partyInfo = recipient.party || {};
        const newLabel = partyInfo.label || recipient.partyName || pos.label;
        const cleanLabelMatch = String(newLabel).match(/\]\s*-\s*(.+)$/);
        const displayLabel = cleanLabelMatch ? cleanLabelMatch[1] : String(newLabel);

        return {
          ...pos,
          label: displayLabel,
          partyId: recipient.partyId || partyInfo.partyId || partyInfo.value || pos.partyId,
          partyName: recipient.partyName || partyInfo.partyName || pos.partyName,
          taxCode: recipient.taxCode || partyInfo.partyTaxId || pos.taxCode,
          emailAddress: recipient.emailAddress || partyInfo.emailAddress || pos.emailAddress,
          contactNumber: recipient.contactNumber || partyInfo.contactNumber || pos.contactNumber,
          representativeName: recipient.representativeName || partyInfo.representativeName || pos.representativeName,
          identityCard: recipient.identityCard || partyInfo.idValue || pos.identityCard,
          roleTypeId: recipient.roleTypeId || pos.roleTypeId,
          internalRecipientId: recipient._internalId || pos.internalRecipientId
        };
      });

      return normalizeLocationSeq(updated);
    });
  }, [documentRecipients]);


  const generateFilePreview = async (file: any) => {
    const fileKey = `${file.name}-${file.mainFileId}`;
    const ext = (file.extension || '').toLowerCase();
    const type = (file.type || file.contentType || '').toLowerCase();
    const isGoogleDoc = type === 'application/vnd.google-apps.document';
    const isPreviewable = ext === 'pdf' || ext === 'docx' || ext === 'doc' || isGoogleDoc;
    const fileId = file.referenceFileId || file.mainFileId;
    if (filePreviews[fileKey]) {
      return filePreviews[fileKey];
    }

    setPreviewLoading(prev => ({ ...prev, [fileKey]: true }));

    try {
      if (isPreviewable) {
        if (!fileId) {
          setFilePreviews(prev => ({ ...prev, [fileKey]: null }));
          setPreviewLoading(prev => ({ ...prev, [fileKey]: false }));
          return null;
        }

        try {
          const blob = await AgreementService.downloadPdf(fileId);
          const arrayBuffer = await blob.arrayBuffer();
          const loadingTask = getDocument({ data: arrayBuffer });
          const pdf = await loadingTask.promise;
          const page = await pdf.getPage(1);
          const scale = 1.2;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          const canvasContext = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          const renderContext = {
            canvasContext,
            viewport,
          };
          await page.render(renderContext).promise;
          const dataUrl = canvas.toDataURL('image/png');
          setFilePreviews(prev => ({ ...prev, [fileKey]: dataUrl }));
          setPreviewLoading(prev => ({ ...prev, [fileKey]: false }));
          return dataUrl;
        } catch (pdfError) {
          setFilePreviews(prev => ({ ...prev, [fileKey]: null }));
          setPreviewLoading(prev => ({ ...prev, [fileKey]: false }));
          return null;
        }
      }
    } catch (error) {
      setPreviewLoading(prev => ({ ...prev, [fileKey]: false }));
    }

    return null;
  };

  useEffect(() => {
    const generatePreviews = async () => {
      for (const file of mainFiles) {
        const ext = (file.extension || '').toLowerCase();
        const type = (file.type || file.contentType || '').toLowerCase();
        const isGoogleDoc = type === 'application/vnd.google-apps.document';
        if (file.googleFileId && (ext === 'pdf' || ext === 'docx' || ext === 'doc' || isGoogleDoc)) {
          await generateFilePreview(file);
        }
      }
    };

    if (mainFiles.length > 0) {
      generatePreviews();
    }
  }, [mainFiles]);
  useEffect(() => {
    if (showSignaturePlacement && mainFiles.length > 0) {
      const mainFile = mainFiles[activeMainFileIndex] || mainFiles[0];
      console.log('Step3 loadPDFToCanvas mainFile:', mainFile);
      console.log('Step3 mainFiles:', mainFiles);
      loadPDFToCanvas(mainFile);
    }
  }, [showSignaturePlacement, mainFiles, activeMainFileIndex]);
  const breadcrumbItems = [
  ];
  React.useEffect(() => {
    CommonService.agreementRoleTypes?.().then((data: any) => {
      if (data?.listData) {
        const allRoles = data.listData.map((it: any) => ({ value: it.roleTypeId, label: it.description }));
        setRoleOptions(allRoles);
        setAvailableRoles(allRoles);
        const signerIds = data.listData
          .filter((it: any) => {
            const desc = String(it?.description || '').toLowerCase();
            return it?.isSigner === 'Y' || /ký|sign/.test(desc) || /bên\s*[ab]/.test(desc);
          })
          .map((it: any) => it.roleTypeId);
        setSignerRoleIds(signerIds);
      }
    }).catch(() => { });

    CustomerService.find('').then((data: any) => {
      if (data?.listData) {
        const uniqueItems: any[] = [];
        const seenIds = new Set();
        data.listData.forEach((item: any) => {
          const id = item.partyId;
          if (id && !seenIds.has(id)) {
            seenIds.add(id);
            const { disabled, ...rest } = item;
            uniqueItems.push({ ...rest, value: id, label: `[${item.pseudoId}] - ${item.partyName || item.label}` });
          }
        });
        setCustomers(uniqueItems);
      }
    }).catch(() => { });

    CommonService.getAgreementType().then((data: any) => {
      const mappedTypes = mapAgreementTypes(data);
      setDocumentTypes(mappedTypes);
    }).catch(() => { });

  }, []);

  useEffect(() => {
    if (detailAgreementTypeOption && detailAgreementTypeOption.value) {
      setDocumentTypes(prev => {
        const exists = prev.some(item => item.value === detailAgreementTypeOption.value);
        if (exists) return prev;
        return [...prev, detailAgreementTypeOption];
      });
    }
  }, [detailAgreementTypeOption]);

  useEffect(() => {
    if (!formData.agreementType) {
      setDocumentTypeItems([]);
      return;
    }
    const selectedType = documentTypes.find((item: any) => item.value === formData.agreementType);
    if (!selectedType) {
      setDocumentTypeItems([]);
      return;
    }
    const configId =
      selectedType.agreementPartyTypeConfId ||
      selectedType.agreementTypeConfId ||
      selectedType.id ||
      selectedType.value;
    if (!configId) {
      setDocumentTypeItems([]);
      return;
    }
    let ignore = false;
    setDocumentTypeItemsLoading(true);
    AgreementConfigService.get(String(configId))
      .then((res: any) => {
        if (ignore) return;
        if (res?.success === 'Y') {
          setDocumentTypeItems(Array.isArray(res?.items) ? res.items : []);
        } else {
          setDocumentTypeItems([]);
          ToastService.error(res?.error || 'Không thể tải cấu hình loại tài liệu');
        }
      })
      .catch((error: any) => {
        if (ignore) return;
        setDocumentTypeItems([]);
        ToastService.error(error?.message || 'Không thể tải cấu hình loại tài liệu');
      })
      .finally(() => {
        if (!ignore) {
          setDocumentTypeItemsLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [documentTypes, formData.agreementType]);

  useEffect(() => {
    if (!Array.isArray(documentTypeItems) || documentTypeItems.length === 0) {
      setDocumentTypeItemMeta({});
      return;
    }
    setDocumentTypeItemMeta(prev => {
      const next: Record<string, { formType?: string | null; receivedDate?: Date | null }> = {};
      documentTypeItems.forEach((item: any, idx: number) => {
        const seq = resolveConfigItemSeq(item, idx);
        if (!seq) return;
        const seqKey = String(seq);
        const prevMeta = prev[seqKey] || {};
        const existingFile = attachedFiles.find((f: any) => Number(f.sequenceNum ?? f.configSequenceNum) === Number(seq));
        const fileFormType = existingFile ? normalizeFormTypeValue(existingFile.agreementFormTypeEnumId) : null;
        const fileDate = existingFile?.agreementFormDate ? new Date(existingFile.agreementFormDate) : null;
        next[seqKey] = {
          formType: normalizeFormTypeValue(prevMeta.formType ?? fileFormType ?? null),
          receivedDate: prevMeta.receivedDate ?? fileDate ?? null
        };
      });
      return next;
    });
  }, [documentTypeItems, attachedFiles, formTypeOptions]);

  const combinedTypeItems = useMemo(() => {
    const items = Array.isArray(documentTypeItems) ? [...documentTypeItems] : [];
    if (Array.isArray(attachedFiles)) {
      attachedFiles.forEach((file: any) => {
        const fileSeqStr = String(file.sequenceNum ?? file.configSequenceNum ?? '');
        const exists = items.find((item: any, idx: number) => {
          const itemSeqStr = String(resolveConfigItemSeq(item, idx) ?? '');
          return itemSeqStr === fileSeqStr;
        });
        if (!exists && fileSeqStr) {
          items.push({
            sequenceNum: file.sequenceNum ?? file.configSequenceNum,
            description: file.name || file.fileName || 'Tệp mẫu đính kèm',
            fileTypeEnumId: file.agreementFormTypeEnumId || file.contentTypeEnumId || file.fileTypeEnumId || '',
            isRequire: 'N',
            isExtraneous: true
          });
        }
      });
      items.sort((a, b) => {
        const seqA = Number(a.sequenceNum ?? a.configSequenceNum ?? 0);
        const seqB = Number(b.sequenceNum ?? b.configSequenceNum ?? 0);
        return seqA - seqB;
      });
    }
    return items;
  }, [documentTypeItems, attachedFiles]);

  useEffect(() => {
    CommonService.getFileTypes()
      .then((res: any) => {
        if (res?.listData && Array.isArray(res.listData)) {
          setFileTypes(res.listData);
        } else if (Array.isArray(res?.list)) {
          setFileTypes(res.list);
        } else if (Array.isArray(res)) {
          setFileTypes(res);
        } else {
          setFileTypes([]);
        }
      })
      .catch((error: any) => {
        console.error('Error loading file types:', error);
        setFileTypes([]);
      });
  }, []);

  const getFileTypeName = (item: any): string => {
    if (!item) return 'Không có mô tả';

    const fileTypeId = item.fileTypeEnumId || item.fileTypeId;
    if (!fileTypeId || fileTypes.length === 0) return item.description || 'Không có mô tả';

    const fileType = fileTypes.find((ft: any) => ft.enumId === fileTypeId || ft.enumCode === fileTypeId);
    return fileType?.description || item.description || 'Không có mô tả';
  };

  const isSignerRole = (roleTypeId: any) => signerRoleIds.includes(roleTypeId);

  const getRoleOptionsForRow = (rowIndex: number) => {
    const selected = documentRecipients[rowIndex]?.roleTypeId;
    const takenIds = documentRecipients
      .map((r, i) => (i === rowIndex ? null : r.roleTypeId))
      .filter((v: any) => v);
    const filtered = roleOptions.filter((o: any) => !takenIds.includes(o.value));
    if (selected && !filtered.some((o: any) => o.value === selected)) {
      const sel = roleOptions.find((o: any) => o.value === selected);
      return sel ? [sel, ...filtered] : filtered;
    }
    return filtered;
  };

  const getCustomerSuggestionsForRow = (rowIndex: number) => {
    const takenPartyIds = documentRecipients
      .map((r, i) => (i === rowIndex ? null : r.party?.value || r.party?.partyId))
      .filter((v: any) => v);
    return customers.filter((c: any) => !takenPartyIds.includes(c.value));
  };

  const addRecipient = () => {
    setDocumentRecipients(prev => [...prev, {
      _internalId: crypto.randomUUID(),
      party: null,
      taxCode: '',
      partyName: '',
      emailAddress: '',
      contactNumber: '',
      representativeName: '',
      identityCard: '',
      roleTypeId: '',
      signingOrder: prev.length + 1
    }]);
  };
  const removeRecipient = (idx: number) => {
    const removed = documentRecipients[idx];
    const next = documentRecipients.filter((_, i) => i !== idx);
    setDocumentRecipients(next);
    if (removed?.roleTypeId) {
      const opt = roleOptions.find((o: any) => o.value === removed.roleTypeId);
      if (opt && !availableRoles.some((o: any) => o.value === opt.value)) {
        setAvailableRoles(prev => [...prev, opt]);
      }
    }
    setHasSigner(next.some(r => isSignerRole(r.roleTypeId)));
  };
  const handleRecipientChange = async (idx: number, field: string, value: any) => {
    let data = [...documentRecipients];
    const oldPartyId = data[idx]?.party?.partyId || data[idx]?.party?.value || data[idx]?.partyId;
    const recipientInternalId = data[idx]?._internalId;
    data[idx][field] = value;
    if (field === 'party') {
      // Reset thông tin liên quan khi đổi đối tượng
      data[idx].taxCode = '';
      data[idx].partyName = '';
      data[idx].emailAddress = '';
      data[idx].contactNumber = '';
      data[idx].representativeName = '';
      data[idx].identityCard = '';
      data[idx].partyId = value?.partyId || value?.value || null;

      // Xóa vị trí ký của đối tượng cũ khi thay đổi đối tượng
      const newPartyId = value?.partyId || value?.value || null;
      if (oldPartyId && String(oldPartyId) !== String(newPartyId || '')) {
        setSignaturePositions(prev =>
          normalizeLocationSeq(prev.filter(pos => {
            // Xóa theo internalRecipientId (chính xác nhất)
            if (pos.internalRecipientId && pos.internalRecipientId === recipientInternalId) {
              return false;
            }
            // Xóa theo partyId cũ nếu không có internalRecipientId
            if (!pos.internalRecipientId && pos.partyId && String(pos.partyId) === String(oldPartyId)) {
              return false;
            }
            return true;
          }))
        );
      }
    }

    if (data.length > roleOptions.length) {
      data = data.slice(0, roleOptions.length);
    }
    if (field === 'party' && value?.partyId) {
      const duplicateIdx = data.findIndex((r, i) => i !== idx && (r.party?.value === value.partyId || r.party?.partyId === value.partyId));
      if (duplicateIdx !== -1) {
        ToastService.error('Đối tượng này đã được chọn ở dòng khác');
        data[idx].party = null;
        setDocumentRecipients(data);
        return;
      }
      try {
        const customer = await CustomerService.get(value.partyId);
        data[idx].taxCode = customer.partyTaxId || '';
        data[idx].partyName = customer.partyName || '';
        data[idx].emailAddress = customer.emailAddress || '';
        data[idx].contactNumber = customer.contactNumber || '';
        data[idx].representativeName = customer.representativeName || '';
        data[idx].identityCard = customer.idValue || '';
        data[idx].partyTypeEnumId = customer.partyTypeEnumId;
        data[idx].party = {
          ...value,
          partyTypeEnumId: customer.partyTypeEnumId
        };
      } catch (e) {
      }
    }

    setDocumentRecipients(data);
    setHasSigner(data.some(r => isSignerRole(r.roleTypeId)));
  };
  const searchCustomers = (event: any) => {
    CustomerService.find(event.query).then((data: any) => {
      const uniqueItems: any[] = [];
      const seenIds = new Set();
      (data.listData || []).forEach((item: any) => {
        const id = item.partyId || item.value;
        if (id && !seenIds.has(id)) {
          seenIds.add(id);
          const { disabled, ...rest } = item;
          uniqueItems.push({ ...rest, value: id, label: `[${item.pseudoId}] - ${item.partyName || item.label}` });
        }
      });
      setCustomers(uniqueItems);
    }).catch(() => { });
  };

  const customerDialog = useCustomerDialog({
    service: CustomerService,
    requireTaxCode: true,
    async onAfterCreate(resp: any) {
      setParty(resp.partyId);
      const customerData = await CustomerService.find('');
      const uniqueItems: any[] = [];
      const seenIds = new Set();
      (customerData.listData || []).forEach((customer: any) => {
        const id = customer.partyId || customer.value;
        if (id && !seenIds.has(id)) {
          seenIds.add(id);
          const { disabled, ...rest } = customer;
          uniqueItems.push({ ...rest, value: id, label: `[${customer.pseudoId}] - ${customer.partyName || customer.label}` });
        }
      });
      setCustomers(uniqueItems);
      const newCustomer = uniqueItems.find((customer: any) => customer.partyId === resp.partyId);
      if (newCustomer && activeRecipientIndex !== null) {
        await handleRecipientChange(activeRecipientIndex, 'party', {
          value: newCustomer.partyId,
          partyId: newCustomer.partyId,
          label: newCustomer.label
        });
      }
    }
  });

  const createCustomer = (idx: number) => {
    console.log('>>> TaiLieu/index.tsx createCustomer click idx:', idx);
    setActiveRecipientIndex(idx);
    const rec = idx >= 0 ? documentRecipients[idx] : null;
    console.log('>>> current data for pre-fill:', rec);
    customerDialog.create({
      partyTypeEnumId: (rec && rec.taxCode) ? 'PtyOrganization' : 'PtyPerson',
      partyName: rec?.partyName || '',
      emailAddress: rec?.emailAddress || '',
      contactNumber: rec?.contactNumber || '',
      taxCode: rec?.taxCode || '',
      idValue: rec?.identityCard || ''
    });
  };

  const validateRequiredAttachedFiles = (): boolean => {
    if (isViewMode) return true;
    if (!Array.isArray(documentTypeItems) || documentTypeItems.length === 0) {
      return true; // Không có config items thì không cần check
    }

    const requiredItems = documentTypeItems.filter((item: any) => {
      const flag = item?.isRequire ?? item?.isRequired ?? item?.required;
      return flag === 'Y' || flag === true;
    });

    if (requiredItems.length === 0) {
      return true; // Không có item bắt buộc
    }

    const missingDescriptions: string[] = [];
    requiredItems.forEach((item: any, idx: number) => {
      const seq = resolveConfigItemSeq(item, idx);
      if (!seq) return;
      const seqKey = String(seq);
      const hasFile = attachedFiles.some((file: any) => {
        const fileSeq = file?.sequenceNum ?? file?.configSequenceNum;
        return Number(fileSeq) === Number(seq);
      });
      if (!hasFile) {
        const desc = item?.description || item?.fileTypeName || `Hạng mục #${seq}`;
        missingDescriptions.push(desc);
      }
    });

    if (missingDescriptions.length > 0) {
      ToastService.error(`Vui lòng tải lên các tệp đính kèm bắt buộc: ${missingDescriptions.join(', ')}`);
      return false;
    }

    return true;
  };

  const validateRecipients = (): boolean => {
    if (isViewMode) return true;
    if (!validateRequiredAttachedFiles()) {
      return false;
    }

    let checker = true;
    let errorMessage = '';

    for (let i = 0; i < documentRecipients.length; i++) {
      const item = documentRecipients[i];

      if (!item.party?.label) {
        checker = false;
        errorMessage = 'Chưa chọn đối tượng ký';
        break;
      }

      if (!item.emailAddress) {
        checker = false;
        errorMessage = `Chưa nhập địa chỉ email cho ${item.party?.label}`;
        break;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(item.emailAddress)) {
        checker = false;
        errorMessage = `Địa chỉ email của ${item.party?.label} chưa nhập đúng định dạng`;
        break;
      }

      if (!item.roleTypeId) {
        checker = false;
        errorMessage = `Chưa chọn vai trò người ký cho ${item.party?.label}`;
        break;
      }

      const trimmedtaxCode = item.taxCode?.trim() || '';
      const taxCodeRegex = /^(\d{10}|\d{12}|\d{13}|\d{10}-\d{3})$/;
      if (trimmedtaxCode.length > 0 && !taxCodeRegex.test(trimmedtaxCode)) {
        checker = false;
        errorMessage = `Mã số thuế của ${item.party?.label} phải có 10, 12, 13 số `;
        break;
      }

      const trimmedContactNumber = item.contactNumber?.trim() || '';
      if (trimmedContactNumber.length > 0 && trimmedContactNumber.length !== 10) {
        checker = false;
        errorMessage = `Số điện thoại của ${item.party?.label} phải có đúng 10 số`;
        break;
      }

      const trimmedIdentityCard = item.identityCard?.trim() || '';
      if (trimmedIdentityCard.length > 0 && ![9, 12].includes(trimmedIdentityCard.length)) {
        checker = false;
        errorMessage = `CCCD/CMND của ${item.party?.label} phải có 9 hoặc 12 số`;
        break;
      }
    }

    if (!checker && errorMessage) {
      ToastService.error(errorMessage);
    }

    return checker;
  };

  useEffect(() => {
    const loadDetail = async () => {
      if (!agreementId) return;
      try {
        const res = await AgreementService.getDetail(agreementId as string);
        if (res && res.success === 'Y') {
          const ag = res.agreement || {};

          const draftStatuses = ['AGT_DRAFT', 'AGT_INIT_DRAFT'];
          const isDraft = draftStatuses.includes(ag.statusId);
          const modeParam = searchParams.get('mode');
          const shouldViewMode = modeParam === 'view' || !isDraft;
          setIsViewMode(shouldViewMode);

          const agreementConfigureEnumId = ag.agreementConfigureEnumId || null;
          setFormData({
            agreementName: ag.agreementName || '',
            agreementType: ag.agreementConfigureEnumId || null,
            effectiveDate: ag.fromDate ? new Date(ag.fromDate) : null,
            expiryDate: ag.thruDate ? new Date(ag.thruDate) : null,
            agreementNo: ag.agreementNo || '',
            agreementNoInt: ag.agreementNoInt || null,
            agreementNoFormatEnumId: ag.agreementNoFormatEnumId || null,
            description: ag.description || '',
            isImportant: ag.isImportant === 'Y',
            isUrgent: ag.isUrgent === 'Y'
          });

          if (ag.templateAgreementId) {
            setTemplateAgreementId(ag.templateAgreementId);
            setIsTemplateMode(true);
          } else {
            setTemplateAgreementId(null);
            setIsTemplateMode(false);
          }
          if (agreementConfigureEnumId) {
            const agreementTypeLabel = ag.agreementConfigureName || ag.agreementTypeName || '';
            setDetailAgreementTypeOption({
              label: agreementTypeLabel || agreementConfigureEnumId,
              value: agreementConfigureEnumId,
              enumCode: agreementConfigureEnumId
            });
          } else {
            setDetailAgreementTypeOption(null);
          }
          setTouchedFields({});
          setValidationTriggered(false);

          const missingPdfFileIds: string[] = [];

          const mappedMain = (res.mainFiles || []).map((f: any) => {
            const fileId = f.fileId || f.contentLocation || f.agreementContentId || f.id || null;
            const extension = getFileExtension(f.fileName) || f.contentType;
            console.log(`[loadDetail] main file: fileName=${f.fileName}, type=${f.contentType}, ext=${extension}, fileId=${fileId}, refId=${f.referenceFileId}`);
            
            // Auto-generate PDF preview if missing
            if (fileId && (extension === 'docx' || extension === 'doc') && !f.referenceFileId) {
              missingPdfFileIds.push(fileId);
              AgreementService.generatePdf(fileId, f.fileName, f.contentType, f.agreementContentId)
                .then(pdfRes => {
                  console.log(`[loadDetail] Auto PDF generation complete for main file ${fileId}:`, pdfRes);
                  if (pdfRes?.referenceFileId) {
                    setMainFiles(prev => prev.map(mf => mf.fileId === fileId ? { ...mf, referenceFileId: pdfRes.referenceFileId } : mf));
                  }
                })
                .finally(() => {
                  setPdfGenerating(prev => {
                    const next = new Set(prev);
                    next.delete(fileId);
                    return next;
                  });
                });
            }

            return {
              agreementContentId: f.agreementContentId || undefined,
              name: f.fileName,
              size: 0,
              type: f.contentType,
              file: undefined,
              extension: extension,
              googleFileId: fileId,
              fileId: fileId,
              id: fileId,
              referenceFileId: f.referenceFileId,
              googleFileUrl: f.contentLocation,
              contentLocation: f.contentLocation,
              isMainFile: true
            };
          });
          const mappedAttached = (res.attachmentFiles || []).map((f: any) => {
            const seqNum = f.sequenceNum ?? f.configSequenceNum ?? f.agreementItemSeq ?? null;
            const normalizedSeq = seqNum !== null && seqNum !== undefined ? Number(seqNum) : null;
            const fileId = f.fileId || f.contentLocation || f.agreementContentId || f.id || null;
            const extension = getFileExtension(f.fileName) || f.contentType;
            console.log(`[loadDetail] attached file: fileName=${f.fileName}, type=${f.contentType}, ext=${extension}, fileId=${fileId}, refId=${f.referenceFileId}`);

            // Auto-generate PDF preview if missing
            if (fileId && (extension === 'docx' || extension === 'doc') && !f.referenceFileId) {
              missingPdfFileIds.push(fileId);
              AgreementService.generatePdf(fileId, f.fileName, f.contentType, f.agreementContentId)
                .then(pdfRes => {
                  console.log(`[loadDetail] Auto PDF generation complete for attached file ${fileId}:`, pdfRes);
                  if (pdfRes?.referenceFileId) {
                    setAttachedFiles(prev => prev.map(af => af.fileId === fileId ? { ...af, referenceFileId: pdfRes.referenceFileId } : af));
                  }
                })
                .finally(() => {
                  setPdfGenerating(prev => {
                    const next = new Set(prev);
                    next.delete(fileId);
                    return next;
                  });
                });
            }

            return {
              agreementContentId: f.agreementContentId || undefined,
              name: f.fileName,
              size: 0,
              type: f.contentType,
              file: undefined,
              extension: extension,
              googleFileId: fileId,
              fileId: fileId,
              id: fileId,
              referenceFileId: f.referenceFileId || null,
              googleFileUrl: f.contentLocation,
              contentLocation: f.contentLocation,
              isMainFile: false,
              sequenceNum: normalizedSeq,
              configSequenceNum: normalizedSeq,
              agreementFormTypeEnumId: f.agreementFormTypeEnumId || null,
              agreementFormDate: f.agreementFormDate || null
            };
          });

          if (missingPdfFileIds.length > 0) {
             console.log('[loadDetail] Found missing PDF previews for files:', missingPdfFileIds);
             setPdfGenerating(prev => {
               const next = new Set(prev);
               missingPdfFileIds.forEach(id => next.add(id));
               return next;
             });
          }

          setMainFiles(mappedMain);
          setAttachedFiles(mappedAttached);
          const apiRecipients = res.agreementParties || res.recipients || res.documentRecipients || res.parties || [];
          if (Array.isArray(apiRecipients) && apiRecipients.length > 0) {
            let normalized = apiRecipients.map((r: any) => ({
              _internalId: crypto.randomUUID(), // Gán ID nội bộ
              party: r.party || (r.partyId ? { value: r.partyId, partyId: r.partyId, label: r.partyName || r.pseudoId || r.partyId } : null),
              partyId: r.partyId,
              taxCode: r.partyTaxId || r.taxCode || '',
              partyName: r.partyName || '',
              emailAddress: r.emailAddress || r.email || '',
              contactNumber: r.contactNumber || r.phone || '',
              representativeName: r.representativeName || r.fullName || '',
              identityCard: r.identityCard || r.idValue || '',
              roleTypeId: r.roleTypeId || r.role || ''
            }));
            try {
              const enrichPromises = normalized.map(async (rec: any) => {
                if (!rec.partyId) return rec;
                try {
                  const customer = await CustomerService.get(rec.partyId);
                  return {
                    ...rec,
                    taxCode: customer.partyTaxId ?? rec.taxCode,
                    partyName: customer.partyName ?? rec.partyName,
                    emailAddress: customer.emailAddress ?? rec.emailAddress,
                    contactNumber: customer.contactNumber ?? rec.contactNumber,
                    representativeName: customer.representativeName ?? rec.representativeName,
                    identityCard: customer.idValue ?? rec.identityCard,
                    partyTypeEnumId: customer.partyTypeEnumId,
                    party: { 
                      value: rec.partyId, 
                      partyId: rec.partyId, 
                      label: `[${customer.pseudoId}] - ${customer.partyName}`,
                      partyTypeEnumId: customer.partyTypeEnumId
                    }
                  };
                } catch { return rec; }
              });
              normalized = await Promise.all(enrichPromises);
            } catch { /* ignore enrich errors */ }
            const finalized = normalized.map((rec: any) => {
              const label = rec.party?.label || (rec.partyName ? rec.partyName : rec.partyId);
              return {
                ...rec,
                party: rec.party && typeof rec.party === 'object'
                  ? { value: rec.party.value || rec.party.partyId || rec.partyId, partyId: rec.party.partyId || rec.partyId, label }
                  : (rec.partyId ? { value: rec.partyId, partyId: rec.partyId, label } : null)
              };
            });
            setDocumentRecipients(finalized);
            var recipientListForPositions = finalized;
          }

          const apiSigners = res.signers || [];
          if ((!apiRecipients || apiRecipients.length === 0) && Array.isArray(apiSigners) && apiSigners.length > 0) {
            try {
              const mapped = apiSigners.map((s: any) => {
                const base: any = {
                  _internalId: crypto.randomUUID(), // Gán ID nội bộ
                  partyId: s.partyId || '',
                  roleTypeId: s.roleTypeId || '',
                };
                if (s.party) {
                  base.party = s.party;
                  base.partyId = s.party.partyId;
                  base.taxCode = s.party.partyTaxId || s.party.taxCode || '';
                  base.partyName = s.party.partyName || '';
                  base.emailAddress = s.party.emailAddress || '';
                  base.contactNumber = s.party.contactNumber || '';
                  base.representativeName = s.party.representativeName || '';
                  base.identityCard = s.party.idValue || s.party.identityCard || '';
                } else {
                  base.party = null;
                  base.partyName = s.partyName || '';
                  base.emailAddress = '';
                  base.contactNumber = '';
                  base.representativeName = '';
                  base.identityCard = '';
                }
                return base;
              });
              setDocumentRecipients(mapped);
              recipientListForPositions = mapped; // Essential for positions mapping below
              if (!selectedSigner && mapped.length > 0) setSelectedSigner({ value: mapped[0].partyId, partyId: mapped[0].partyId, roleTypeId: mapped[0].roleTypeId, label: mapped[0].party?.label || mapped[0].partyName } as any);
            } catch {/* ignore */ }
          }
          const agreementPartiesStatus: Record<string, string> = {};
          (res?.agreementParties || res?.agreement_parties || []).forEach((p: any) => {
            if (p?.partyId) {
              agreementPartiesStatus[String(p.partyId)] = p.statusId || p.status_id || '';
            }
          });

          const apiPositions = res.signaturePositions || res.signature_positions || [];
          let mappedPositions: any[] = [];
          if (Array.isArray(apiPositions) && apiPositions.length > 0) {
            const recipientsRef = recipientListForPositions || documentRecipients;
            const getPartyLabel = (pid: any) => {
              const r = Array.isArray(recipientsRef) ? recipientsRef.find((x: any) => x.partyId === pid) : undefined;
              const raw = r?.party?.label || r?.partyName || '';
              const m = String(raw).match(/\]\s*-\s*(.+)$/);
              return m ? m[1] : String(raw);
            };
            mappedPositions = apiPositions
              .map((p: any, idx: number) => {
                const pid = p.partyId || p.party_id || null;
                const partyLabel = p.partyName || p.party_name || getPartyLabel(pid);

                // Tìm recipient tương ứng để lấy _internalId
                const rec = Array.isArray(recipientsRef)
                  ? recipientsRef.find((r: any) => String(r.partyId) === String(pid))
                  : null;

                return {
                  locationSeqId: p.locationSeqId || p.location_seq_id || String(idx + 1).padStart(2, '0'),
                  pageIndex: p.pageNumber ? Number(p.pageNumber) - 1 : Number(p.pageIndex ?? p.page_index ?? 0),
                  x: Number(p.ulx ?? p.x ?? 0),
                  y: Number(p.uly ?? p.y ?? 0),
                  widthPx: Number(p.widthPx ?? p.width_px ?? p.width ?? 200),
                  heightPx: Number(p.heightPx ?? p.height_px ?? p.height ?? 65),
                  fontSize: Number(p.fontSize ?? p.font_size ?? 12),
                  partyId: pid,
                  internalRecipientId: rec?._internalId || null,
                  roleTypeId: p.roleTypeId || p.role_type_id || null,
                  partyName: partyLabel,
                  label: partyLabel,
                  signatureTypeEnumId: p.signatureTypeEnumId || p.signature_type_enum_id || 'AgtDigitalVerified',
                  agreementContentId: p.agreementContentId || p.agreement_content_id || null,
                  fileId: p.fileId || p.file_id || null
                };
              })
              .filter((pos: any) => {
                if (!pos.partyId) return true;
                const status = agreementPartiesStatus[String(pos.partyId)];
                return status !== 'AGT_PTY_SIGNED';
              });
          }
          if (mappedPositions.length > 0) setSignaturePositions(normalizeLocationSeq(mappedPositions));
        } else {
          ToastService.error(res?.message || 'Không tải được thông tin tài liệu');
        }
      } catch (e) {
        ToastService.error(e?.message || 'Lỗi khi tải thông tin tài liệu');
      } finally { }
    };
    loadDetail();
  }, [agreementId]);
  const validateForm = () => {
    if (isViewMode) return true;
    let valid = true;
    let errorMessage = '';

    // Validate tên tài liệu
    if (!formData.agreementName || formData.agreementName.trim() === '') {
      valid = false;
      errorMessage = 'Vui lòng nhập Tên tài liệu';
    }
    // Validate loại tài liệu
    else if (formData.agreementType === null) {
      valid = false;
      errorMessage = 'Vui lòng chọn Loại tài liệu';
    }
    else if (mainFiles.length === 0) {
      valid = false;
      errorMessage = 'Vui lòng tải lên tệp tài liệu trước khi ký';
    }
    else if (!formData.agreementNo || formData.agreementNo.trim() === '') {
      valid = false;
      errorMessage = 'Vui lòng nhập Mã tài liệu';
    }
    else if (formData.effectiveDate) {
      const now = new Date();
      const effective = new Date(formData.effectiveDate as any);
      const nowPlusOneMinute = new Date(now.getTime() + 60 * 1000); // Cho phép sai số 1 phút

      if (effective.getTime() > nowPlusOneMinute.getTime()) {
        valid = false;
        errorMessage = 'Ngày hiệu lực không được lớn hơn thời điểm hiện tại';
      }
    }

    if (valid && formData.expiryDate) {
      const now = new Date();
      const expiry = new Date(formData.expiryDate as any);

      if (formData.effectiveDate) {
        const effective = new Date(formData.effectiveDate as any);
        const effectivePlusOneHour = new Date(effective.getTime() + 60 * 60 * 1000);
        const nowPlusOneHour = new Date(now.getTime() + 60 * 60 * 1000);
        const minExpiryDate = new Date(Math.max(effectivePlusOneHour.getTime(), nowPlusOneHour.getTime()));
        if (expiry.getTime() <= minExpiryDate.getTime()) {
          valid = false;
          errorMessage = 'Ngày hết hiệu lực phải lớn hơn ngày hiệu lực ít nhất 1 giờ';
        }
      } else {
        const nowPlusOneHour = new Date(now.getTime() + 60 * 60 * 1000);
        if (expiry.getTime() <= nowPlusOneHour.getTime()) {
          valid = false;
          errorMessage = 'Ngày hết hiệu lực phải lớn hơn thời điểm hiện tại ít nhất 1 giờ';
        }
      }
    }
    if (valid && Array.isArray(documentTypeItems) && documentTypeItems.length > 0) {
      const missingMethod: string[] = [];
      documentTypeItems.forEach((item: any, idx: number) => {
        const seq = resolveConfigItemSeq(item, idx);
        if (!seq) return;
        const seqKey = String(seq);

        const isRequired = item?.isRequire === 'Y' || item?.isRequired === true || item?.required === true;
        const hasFile = attachedFiles.some((f: any) => {
          const fileSeq = f?.sequenceNum ?? f?.configSequenceNum;
          return Number(fileSeq) === Number(seq);
        });

        // Bỏ qua nếu không bắt buộc và chưa có file
        if (!isRequired && !hasFile) return;

        const meta = documentTypeItemMeta[seqKey];
        if (!meta?.formType) {
          const desc = item?.description || item?.fileTypeName || `Hạng mục #${seq}`;
          missingMethod.push(desc);
        }
      });

      if (missingMethod.length > 0) {
        valid = false;
        errorMessage = `Vui lòng chọn Hình thức cho: ${missingMethod.join(', ')}`;
      }
    }

    if (valid && Array.isArray(documentTypeItems) && documentTypeItems.length > 0) {
      const missingDateItems: string[] = [];
      documentTypeItems.forEach((item: any, idx: number) => {
        const seq = resolveConfigItemSeq(item, idx);
        if (!seq) return;
        const seqKey = String(seq);

        const meta = documentTypeItemMeta[seqKey];
        const file = attachedFiles.find((f: any) => {
          const fileSeq = f?.sequenceNum ?? f?.configSequenceNum;
          return Number(fileSeq) === Number(seq);
        });

        const normalizedFormType =
          meta?.formType ??
          (file?.agreementFormTypeEnumId ? normalizeFormTypeValue(file.agreementFormTypeEnumId) : null);
        if (normalizedFormType === 'ATTACH_SCAN') {
          const effectiveDateValue =
            meta?.receivedDate ??
            (file?.agreementFormDate ? new Date(file.agreementFormDate) : null);
          if (!effectiveDateValue) {
            const desc = item?.description || item?.fileTypeName || `Hạng mục #${seq}`;
            missingDateItems.push(desc);
          }
        }
      });
      if (missingDateItems.length > 0) {
        valid = false;
        errorMessage = `Vui lòng nhập Ngày nhận cho: ${missingDateItems.join(', ')}`;
      }
    }

    if (valid && Array.isArray(documentTypeItems) && documentTypeItems.length > 0) {
      const requiredItems = documentTypeItems.filter((item: any) => {
        const flag = item?.isRequire ?? item?.isRequired ?? item?.required;
        return flag === 'Y' || flag === true;
      });
      if (requiredItems.length > 0) {
        const missingDescriptions: string[] = [];
        requiredItems.forEach((item: any, idx: number) => {
          const seq = resolveConfigItemSeq(item, idx);
          if (!seq) return;
          const seqKey = String(seq);
          const hasFile = attachedFiles.some((file: any) => {
            const fileSeq = file?.sequenceNum ?? file?.configSequenceNum;
            return Number(fileSeq) === Number(seq);
          });
          if (!hasFile) {
            const desc = item?.description || item?.fileTypeName || `Hạng mục #${seq}`;
            missingDescriptions.push(desc);
          }
        });
        if (missingDescriptions.length > 0) {
          valid = false;
          errorMessage = `Vui lòng tải lên các tệp bắt buộc: ${missingDescriptions.join(', ')}`;
        }
      }
    }

    if (!valid && errorMessage) {
      ToastService.error(errorMessage);
    }

    setIsValid(valid);
    return valid;
  };

  const getEffectiveMaxDate = () => {
    return new Date(); // Ngày hiệu lực không được lớn hơn thời điểm hiện tại
  };

  const getExpiryMinDate = () => {
    const now = new Date();
    const minFromNow = new Date(now.getTime() + 60 * 60 * 1000);

    if (formData.effectiveDate) {
      const effective = new Date(formData.effectiveDate as any);
      const minFromEffective = new Date(effective.getTime() + 60 * 60 * 1000);
      return new Date(Math.max(minFromNow.getTime(), minFromEffective.getTime()));
    }
    return minFromNow;
  };

  const handleInputChange = (field: string, value: any, options?: { skipTouch?: boolean; skipAutoCode?: boolean }) => {
    let newData = { ...formData, [field]: value };

    if (field === 'agreementType' && formData.agreementType !== value) {
      setAttachedFiles(prev => prev.filter(f => f.sequenceNum === null || typeof f.sequenceNum === 'undefined'));
      setDocumentTypeItemMeta({});
      newData = { ...newData, agreementNo: '' };
    }

    setFormData(newData);

    if (field === 'agreementType' && formData.agreementType !== value && !options?.skipAutoCode) {
      generateDocumentCode(value);
    }

    if (!options?.skipTouch) {
      setTouchedFields(prev => ({ ...prev, [field]: true }));
    }

    markDirty();
  };

  const handleCheckboxChange = (field: 'isImportant' | 'isUrgent', checked: boolean) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
    markDirty();
  };

  // Chỉ xóa trên client để cập nhật UI, không gọi server
  const handleRemoveLocalFile = (file: any) => {
    const targetId = file.fileId || file.googleFileId || file.id || null;
    const matchFile = (candidate: any) => {
      const candidateId = candidate.fileId || candidate.googleFileId || candidate.id || null;
      return targetId && candidateId && targetId === candidateId;
    };

    if (file.isMainFile) {
      setMainFiles(prev => prev.filter(f => !matchFile(f)));
    } else {
      setAttachedFiles(prev => prev.filter(f => !matchFile(f)));
    }
    markDirty();
  };
  const handleFileUpload = async (event: any) => {
    const files = event.files;
    if (files && files.length > 0) {
      setFileOperationLoading(true);
      const validFiles: any[] = [];
      const invalidFiles: string[] = [];

      Array.from(files).forEach((file: any) => {
        const extension = getFileExtension(file.name);
        const allowedExtensions = ['pdf', 'docx', 'doc'];

        if (allowedExtensions.includes(extension)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        ToastService.error(`Các file sau không đúng định dạng: ${invalidFiles.join(', ')}. Chỉ hỗ trợ PDF, DOCX, DOC.`);
      }

      for (const file of validFiles) {
        try {
          const mapped = await uploadFileToGoogle(file, { isMain: true });
          if (mapped) {
            setMainFiles(prev => [...prev, mapped]);
          } else {
            ToastService.error(`Không thể upload ${file.name}`);
          }
        } catch (e) {
          ToastService.error(`Lỗi khi upload ${file.name}`);
        }

      }
      markDirty();
      setFileOperationLoading(false);
    }
  };

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const uploadFileToGoogle = async (file: File, options: { isMain: boolean; sequenceNum?: number | null }) => {
    const isMain = options.isMain;
    const seq = options.sequenceNum;
    const mapResponse = (res: any) => {
      if (!res) return null;
      const fileId = res.fileId || null;
      return {
        name: res.fileName || file.name,
        size: (file as any).size,
        type: (file as any).type,
        file: undefined,
        extension: res.format || getFileExtension(file.name),
        googleFileId: fileId,
        fileId: fileId,
        id: fileId,
        googleFileUrl: res.fileUrl,
        referenceFileId: res.referenceFileId || undefined,
        isMainFile: isMain,
        sequenceNum: seq ?? undefined,
        configSequenceNum: seq ?? undefined
      };
    };

    const res = await AgreementService.uploadFile(file);
    console.log('uploadFileToGoogle response:', res);
    if (res && (res.success === 'Y' || res.fileId)) {
      // Tự động sinh PDF preview nếu là file doc/docx (chạy ngầm)
      const ext = getFileExtension(file.name);
      if (ext === 'docx' || ext === 'doc') {
        AgreementService.generatePdf(res.fileId, res.fileName || file.name, (file as any).type, res.agreementContentId)
          .catch(pdfErr => console.error('Lỗi sinh PDF preview:', pdfErr));
      }

      const mapped = mapResponse(res);
      console.log('uploadFileToGoogle mapped:', mapped);
      if (mapped) return mapped;
    }
    throw new Error(res?.errors || res?.error || 'Upload thất bại');
  };
  const getIconByExtension = (extOrType?: string): { icon: string; color: string } => {
    const e = (extOrType || '').toLowerCase();
    if (e === 'pdf') return { icon: 'pi pi-file-pdf', color: 'text-red-500' };
    if (
      e === 'doc' ||
      e === 'docx' ||
      e === 'application/vnd.google-apps.document' ||
      e === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      e === 'application/msword'
    )
      return { icon: 'pi pi-file-word', color: 'text-blue-500' };
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(e)) return { icon: 'pi pi-image', color: 'text-green-500' };
    if (['xlsx', 'xls', 'csv'].includes(e)) return { icon: 'pi pi-file-excel', color: 'text-green-600' };
    if (['ppt', 'pptx'].includes(e)) return { icon: 'pi pi-file', color: 'text-orange-500' };
    if (['zip', 'rar'].includes(e)) return { icon: 'pi pi-file-archive', color: 'text-yellow-600' };
    if (['txt', 'json', 'xml', 'html'].includes(e)) return { icon: 'pi pi-file', color: 'text-gray-600' };
    return { icon: 'pi pi-file', color: 'text-blue-500' };
  };

  const ensureMainFile = (files: any[]) => {
    return files;
  };

  const setMainFile = (idx: number) => {
    setMainFiles(prev => prev.map((f, i) => ({ ...f, isMainFile: i === idx })));
    markDirty();
  };

  const handleCreateGoogleFile = () => {
    const tempId = `temp-gdoc-${Date.now()}`;
    const placeholder = {
      name: formData.agreementName || 'Tài liệu mới',
      size: 0,
      type: 'application/vnd.google-apps.document',
      fileId: tempId,
      id: tempId,
      uploading: true,
      extension: 'docx',
      isMainFile: true
    };

    setMainFiles(prev => [...prev, placeholder]);

    Oauth2GoogleService.create({ agreementId: agreementId, fileName: formData.agreementName || 'Tài liệu mới' })
      .then(res => {
        if (res && res.fileId) {
          const mapped = {
            name: res.fileName || formData.agreementName || 'Tài liệu mới',
            size: 0,
            type: 'application/vnd.google-apps.document',
            fileId: res.fileId,
            id: res.fileId,
            googleFileId: res.fileId,
            googleFileUrl: `https://docs.google.com/document/d/${res.fileId}/edit`,
            extension: 'docx',
            isMainFile: true
          };
          setMainFiles(prev => prev.map(f => f.fileId === tempId ? mapped : f));
          setGoogleFileId(res.fileId);
          setShowGDoc(true);
          setDisplayEditor({ fileId: res.fileId });
          markDirty();
        } else {
          ToastService.error('Không thể tạo file Google Doc');
          setMainFiles([]);
        }
      })
      .catch(err => {
        ToastService.error('Có lỗi xảy ra khi tạo file Google Doc');
        setMainFiles([]);
      });
  };
  const handleComputerUpload = () => {
    if (isViewMode) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx';
    input.multiple = true;
    input.onchange = (e: any) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const fileList = Array.from(files);
        
        fileList.forEach((file: any) => {
          const extension = getFileExtension(file.name);
          const allowedExtensions = ['pdf', 'docx', 'doc'];

          if (!allowedExtensions.includes(extension)) {
            ToastService.error(`File ${file.name} không đúng định dạng. Chỉ hỗ trợ PDF, DOCX, DOC.`);
            return;
          }

          const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          const placeholder = {
            name: file.name,
            size: file.size,
            type: file.type,
            fileId: tempId,
            id: tempId,
            uploading: true,
            extension: extension,
            isMainFile: true
          };

          setMainFiles(prev => [...prev, placeholder]);

          AgreementService.uploadFile(file)
            .then(res => {
              if (res && (res.success === 'Y' || res.fileId)) {
                const mapped = {
                  name: res.fileName || file.name,
                  size: file.size,
                  type: file.type,
                  fileId: res.fileId,
                  id: res.fileId,
                  googleFileId: res.fileId,
                  googleFileUrl: res.fileUrl,
                  extension: res.format || extension,
                  isMainFile: true,
                  referenceFileId: res.referenceFileId
                };

                setMainFiles(prev => prev.map(f => f.fileId === tempId ? mapped : f));

                // Sinh PDF preview ngầm
                const ext = (mapped.extension || '').toLowerCase();
                if ((ext === 'docx' || ext === 'doc') && !mapped.referenceFileId) {
                  setPdfGenerating(prev => new Set(prev).add(res.fileId));
                  AgreementService.generatePdf(res.fileId, mapped.name, mapped.type, res.agreementContentId)
                    .then(pdfRes => {
                      if (pdfRes?.referenceFileId) {
                        setMainFiles(prev => prev.map(f => f.fileId === res.fileId ? { ...f, referenceFileId: pdfRes.referenceFileId } : f));
                      }
                    })
                    .finally(() => {
                      setPdfGenerating(prev => {
                        const next = new Set(prev);
                        next.delete(res.fileId);
                        return next;
                      });
                    });
                }
                markDirty();
              }
            })
            .catch(err => {
              ToastService.error(`Lỗi khi tải tệp ${file.name} lên`);
              setMainFiles(prev => prev.filter(f => f.fileId !== tempId));
            });
        });
      }
    };
    input.click();
  };


  const generateThumbnail = async (fileId: string, fileName: string, fallbackId: string) => {
    const fileKey = `${fileName}-${fallbackId}`;
    if (filePreviews[fileKey] || previewLoading[fileKey]) return;

    console.log(`[Thumbnail] Đang khởi tạo preview cho: ${fileName} (fileId: ${fileId})`);
    setPreviewLoading(prev => ({ ...prev, [fileKey]: true }));
    try {
      const pdfBlob = await AgreementService.downloadPdf(fileId);
      console.log(`[Thumbnail] Đã tải xong PDF cho: ${fileName}`);
      const arrayBuffer = await pdfBlob.arrayBuffer();
      const loadingTask = getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);

      const viewport = page.getViewport({ scale: 0.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        await page.render({ canvasContext: context, viewport }).promise;
        const dataUrl = canvas.toDataURL();
        setFilePreviews(prev => ({ ...prev, [fileKey]: dataUrl }));
        console.log(`[Thumbnail] Đã tạo xong ảnh preview cho: ${fileName}`);
      }
    } catch (error: any) {
      console.error(`[Thumbnail] Lỗi tạo preview cho ${fileName}:`, error);
    } finally {
      setPreviewLoading(prev => ({ ...prev, [fileKey]: false }));
    }
  };

  useEffect(() => {
    mainFiles.forEach(file => {
      const ext = (file.extension || '').toLowerCase();
      // Với PDF gốc, source chính là nó. Với Word, source là referenceFileId (PDF đã convert)
      const sourceId = file.referenceFileId || (ext === 'pdf' ? (file.fileId || file.googleFileId || file.id) : null);
      
      if (sourceId && !file.uploading) {
        const fallbackId = file.googleFileId || file.fileId || file.id;
        const fileKey = `${file.name}-${fallbackId}`;
        if (!filePreviews[fileKey] && !previewLoading[fileKey]) {
          generateThumbnail(sourceId, file.name, fallbackId);
        }
      }
    });
  }, [mainFiles]);




  const handleDocumentTypeItemUpload = (item: any, idx: number) => {

    if (isViewMode) return;
    const seq = resolveConfigItemSeq(item, idx);
    if (!seq) {
      ToastService.error('Không xác định được hạng mục file.');
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx';
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        name: file.name,
        size: file.size,
        type: file.type,
        fileId: tempId,
        id: tempId,
        uploading: true,
        extension: file.name.split('.').pop()?.toLowerCase(),
        isMainFile: false,
        sequenceNum: seq,
        configSequenceNum: seq
      };

      setAttachedFiles(prev => {
        const filtered = prev.filter(f => Number(f?.sequenceNum ?? f?.configSequenceNum) !== Number(seq));
        const normalFiles = filtered.filter(f => f.sequenceNum === null || typeof f.sequenceNum === 'undefined');
        const configFiles = filtered.filter(f => !(f.sequenceNum === null || typeof f.sequenceNum === 'undefined'));
        return [...configFiles, placeholder, ...normalFiles];
      });

      AgreementService.uploadFile(file)
        .then(res => {
          if (res && (res.success === 'Y' || res.fileId)) {
            const mapped = {
              name: res.fileName || file.name,
              size: file.size,
              type: file.type,
              fileId: res.fileId,
              id: res.fileId,
              googleFileId: res.fileId,
              googleFileUrl: res.fileUrl,
              extension: res.format || file.name.split('.').pop()?.toLowerCase(),
              sequenceNum: seq,
              isMainFile: false,
              referenceFileId: res.referenceFileId
            };

            setAttachedFiles(prev => prev.map(f => f.fileId === tempId ? mapped : f));

            // Sinh PDF preview ngầm nếu là doc/docx
            const ext = (mapped.extension || '').toLowerCase();
            if ((ext === 'docx' || ext === 'doc') && !mapped.referenceFileId) {
              setPdfGenerating(prev => new Set(prev).add(res.fileId));
              AgreementService.generatePdf(res.fileId, mapped.name, mapped.type, res.agreementContentId)
                .then(pdfRes => {
                  if (pdfRes?.referenceFileId) {
                    setAttachedFiles(prev => prev.map(f => f.fileId === res.fileId ? { ...f, referenceFileId: pdfRes.referenceFileId } : f));
                  }
                })
                .finally(() => {
                  setPdfGenerating(prev => {
                    const next = new Set(prev);
                    next.delete(res.fileId);
                    return next;
                  });
                });
            }
            ToastService.success(`Đã tải lên tệp cho hạng mục #${seq}`);
            markDirty();
          }
        })
        .catch(err => {
          ToastService.error('Lỗi khi tải tệp lên');
          setAttachedFiles(prev => prev.filter(f => f.fileId !== tempId));
        });
    };
    input.click();
  };

  const handleDocumentTypeItemMetaChange = (seqKey: string, field: 'formType' | 'receivedDate', value: any) => {
    const nextValue = field === 'formType' ? normalizeFormTypeValue(value) : value;
    setDocumentTypeItemMeta(prev => ({
      ...prev,
      [seqKey]: {
        ...prev[seqKey],
        [field]: nextValue
      }
    }));
    markDirty();
  };
  const extractViewFileId = (file: any) => {
    console.log('extractViewFileId (TaiLieu) file:', file);
    return file?.referenceFileId || null;
  };

  const extractOriginalFileId = (file: any) => {
    console.log('extractOriginalFileId (TaiLieu) file:', file);
    // Bỏ qua referenceFileId vì đây là bản PDF chỉ để hiển thị
    if (file?.fileId) return file.fileId;
    if (file?.googleFileId) return file.googleFileId;

    const location = file?.contentLocation || file?.googleFileUrl;
    if (location && typeof location === 'string') {
      const match = location.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (match && match[1]) return match[1];
    }

    return file?.agreementContentId || file?.id || file?.contentId || file?.description;
  };

  const handleViewFile = async (file: any) => {
    console.log('handleViewFile (TaiLieu) click file:', file);
    let targetId = extractViewFileId(file);
    if (!targetId) {
      ToastService.error('Đang chuẩn bị bản xem trước, vui lòng đợi ít giây...');
      try {
        const fileId = file.fileId || file.googleFileId || file.id;
        const res = await AgreementService.generatePdf(fileId, file.name || file.fileName, file.contentType, file.agreementContentId);
        if (res && (res.referenceFileId)) {
          targetId = res.referenceFileId;
        }
      } catch (err) {
        console.error('Lỗi khi chuẩn bị file PDF:', err);
      }
    }

    if (!targetId) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Không tìm thấy bản PDF phù hợp để xem. Vui lòng thử lại sau ít phút!' });
      } else {
        ToastService.error('Không tìm thấy bản PDF phù hợp để xem. Vui lòng thử lại sau ít phút!');
      }
      return;
    }
    setSelectedFile(file);
    setViewerUrl(targetId);
    setCurrentPage(1);
    setShowFileViewer(true);
  };

  const handleEditFile = async (file: any) => {
    console.log('>>> handleEditFile (Hybrid Storage):', { file, agreementId });
    const fileId = extractOriginalFileId(file);
    if (!fileId) {
      console.error('>>> handleEditFile error: No fileId found for', file);
      ToastService.error('File này không thể chỉnh sửa');
      return;
    }

    try {
      setFileOperationLoading(true);
      console.log('>>> Calling openEditor with:', { agreementId, fileId });
      // Gọi service openEditor để đồng bộ từ MinIO sang GD nếu cần
      const result = await AgreementService.openEditor(agreementId, fileId);
      console.log('>>> openEditor result:', result);

      if (result && result.googleFileId) {
        setGoogleFileId(result.googleFileId);
        setDisplayEditor({ 
          fileId: result.googleFileId, 
          originalFileId: fileId, // Lưu lại ID gốc trên MinIO để lưu đè sau này
          fileName: file.name || file.fileName 
        });
      } else {
        ToastService.error(result?.error || 'Không thể mở quyền chỉnh sửa');
      }
    } catch (error) {
      ToastService.error('Có lỗi xảy ra khi mở chỉnh sửa');
    } finally {
      setFileOperationLoading(false);
    }
  };

  const handleCloseFile = async (file: any) => {
    if (!file.googleFileId) {
      ToastService.error('File này không thể đóng quyền');
      return;
    }

    try {
      await AgreementService.closeContent(agreementId, file.googleFileId);
    } catch (error) {
      ToastService.error('Có lỗi xảy ra khi đóng File');
    }
  };

  useEffect(() => {
    if (!displayEditor && selectedFile?.googleFileId) {
      AgreementService.closeContent(agreementId, selectedFile.googleFileId).catch(() => undefined);
    }
  }, [displayEditor, selectedFile, agreementId]);


  const handleDownloadFile = async (file: any) => {
    try {
      const fileId = extractOriginalFileId(file);
      if (!fileId) throw new Error('missing fileId');

      const blob = await AgreementService.downloadFile(fileId, { original: true, contentLocation: file.contentLocation || file.googleFileUrl });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;

      // Extract filename from blob (from Content-Disposition header) if available
      const blobFileName = (blob as any).fileName;

      // Priority: file.name > file.fileName > blobFileName > 'document'
      // Always use the name from the file being downloaded, not from mainFile
      const fileName = file.name || file.fileName || blobFileName || 'document';

      // Only add extension if filename doesn't already have one
      const hasExtension = fileName.includes('.') && /\.\w+$/.test(fileName);

      // Use extension from the file being downloaded
      const ext = (file.extension && !hasExtension ? ('.' + file.extension) : '') || '';

      const finalFileName = hasExtension ? fileName : (fileName + ext);

      link.download = finalFileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      ToastService.error('Không thể tải xuống file này');
    }
  };

  const getFileMenuItems = (file: any) => {
    const isUploading = !!file.uploading;
    const targetIdCheck = file.fileId || file.googleFileId || file.agreementContentId || file.id;
    const isPdfGenerating = pdfGenerating.has(targetIdCheck);
    const isPreparing = isUploading || isPdfGenerating;
    
    const allItems = [
      {
        label: isPdfGenerating ? 'Đang chuẩn bị...' : 'Xem',
        icon: isPdfGenerating ? 'pi pi-spin pi-spinner' : 'pi pi-eye',
        command: (e: any) => {
          e?.originalEvent?.stopPropagation?.();
          if (isPdfGenerating) {
            ToastService.error('Bản xem trước đang được xử lý, vui lòng đợi xíu nha!');
            return;
          }
          handleViewFile(file);
        },
        disabled: isPdfGenerating || isUploading
      },
      {
        label: 'Chỉnh sửa',
        icon: 'pi pi-pencil',
        command: (e: any) => {
          e?.originalEvent?.stopPropagation?.();
          handleEditFile(file);
        },
        disabled: file.extension === 'pdf' || isUploading,
        hidden: isViewMode
      },
      {
        label: 'Tải xuống',
        icon: 'pi pi-download',
        command: (e: any) => {
          e?.originalEvent?.stopPropagation?.();
          handleDownloadFile(file);
        },
        disabled: isUploading
      },
      {
        separator: true,
        hidden: isViewMode
      },
      {
        label: 'Xóa',
        icon: 'pi pi-trash',
        command: (e: any) => {
          e?.originalEvent?.stopPropagation?.();
          handleRemoveLocalFile(file);
        },
        template: (item: any, options: any) => {
          return (
            <button 
              onClick={(e) => options.onClick(e)} 
              className="p-menuitem-link appearance-none border-none bg-transparent w-full text-left flex align-items-center gap-2 px-3 py-2" 
              style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--surface-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <span className={`p-menuitem-icon ${item.icon} text-red-500`}></span>
              <span className="p-menuitem-text text-red-500 font-medium">{item.label}</span>
            </button>
          )
        },
        hidden: isViewMode,
        disabled: isUploading
      }
    ];
    return allItems.filter(item => !item.hidden);
  };

  const toggleAttachedFiles = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAttachedFilesExpanded(!isAttachedFilesExpanded);
  };

  const handleFinalSubmit = async () => {
    try {
      if (!formData.agreementName || !formData.agreementName.trim()) {
        ToastService.error('Vui lòng nhập Tên tài liệu');
        return;
      }
      if (!formData.agreementType) {
        ToastService.error('Vui lòng chọn Loại tài liệu');
        return;
      }
      const mainFile = mainFiles.find(f => f.isMainFile);
      if (!mainFile) {
        ToastService.error('Không tìm thấy file chính để lưu vị trí ký');
        return;
      }

      const missingSignerPositions = documentRecipients.filter(s => {
        const sId = s.partyId || s.party?.partyId || s.party?.value;
        return !signaturePositions.some(p => String(p.partyId) === String(sId));
      });
      if (missingSignerPositions.length > 0) {
        const missingNames = missingSignerPositions
          .map(s => s.party?.label || s.partyName || s.partyId)
          .join(', ');
        ToastService.error(`Vui lòng thêm vị trí ký cho: ${missingNames}`);
        return;
      }

      const normalizedPositions = normalizeLocationSeq(signaturePositions);

      const formatDateForAPI = (date: Date | null) => {
        if (!date) return undefined;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const mainFilesArray = mainFiles.map(file => ({
        agreementContentId: file.agreementContentId || undefined,
        fileName: file.name,
        fileId: file.googleFileId || file.fileId || file.id,
        referenceFileId: file.referenceFileId || undefined,
        contentType: file.type,
        contentLocation: file.googleFileUrl || file.contentLocation || undefined
      }));

      // Tạo attachmentFiles array
      const attachmentFilesArray = attachedFiles
        .filter(file => file.sequenceNum !== null && file.sequenceNum !== undefined)
        .map(file => {
          const seqKey = String(file.sequenceNum);
          const meta = documentTypeItemMeta[seqKey] || {};
          const receivedDateValue =
            meta?.receivedDate ??
            (file.agreementFormDate ? new Date(file.agreementFormDate) : null);
          const selectedFormType =
            meta?.formType ??
            normalizeFormTypeValue(file.agreementFormTypeEnumId) ??
            null;
          return {
            agreementContentId: file.agreementContentId || undefined,
            fileName: file.name,
            fileId: file.googleFileId || file.fileId || file.id,
            referenceFileId: file.referenceFileId || undefined,
            contentType: file.type,
            contentLocation: file.googleFileUrl || file.contentLocation || undefined,
            sequenceNum: file.sequenceNum,
            agreementFormTypeEnumId: selectedFormType || file.agreementFormTypeEnumId || null,
            agreementFormDate: receivedDateValue ? formatDateForAPI(receivedDateValue) : (file.agreementFormDate ? formatDateForAPI(new Date(file.agreementFormDate)) : undefined)
          };
        });

      // Tạo agreementParties array
      const agreementPartiesArray = documentRecipients.map((rec: any) => ({
        partyId: rec.partyId || rec.party?.partyId || rec.party?.value,
        roleTypeId: rec.roleTypeId,
        statusId: rec.statusId || 'AGT_PTY_UNSIGNED'
      }));

      // Tạo signaturePositions array
      const positions = normalizedPositions.map(pos => {
        const pageIdx = Number(pos.pageIndex);
        const canvas = canvasRefs.current[pageIdx];
        const pageWidth = canvas?.width || 0;
        const pageHeight = canvas?.height || 0;
        const widthPx = Number(pos.widthPx);
        const heightPx = Number(pos.heightPx);
        const fontSize = Number(pos.fontSize);
        const x = Number(pos.x);
        const y = Number(pos.y);

        // Tìm thông tin party đầy đủ từ documentRecipients
        const partyId = pos.partyId || (selectedSigner && (selectedSigner.partyId || selectedSigner.value));
        const recipient = documentRecipients.find((r: any) =>
          r.partyId === partyId ||
          r.party?.partyId === partyId ||
          r.party?.value === partyId
        );

        // Lấy toàn bộ thông tin party
        const partyInfo = recipient?.party || {};
        // Đảm bảo fileId hợp lệ, nếu thiếu hoặc là ID tạm thời thì lấy từ file chính đang active
        let finalFileId = pos.fileId;
        if (!finalFileId || String(finalFileId).startsWith('temp-')) {
          const primaryFile = mainFiles[activeMainFileIndex] || mainFiles[0];
          finalFileId = primaryFile?.googleFileId || primaryFile?.fileId || primaryFile?.id;
        }

        return {
          locationSeqId: pos.locationSeqId,
          fileId: finalFileId,
          agreementContentId: pos.agreementContentId || undefined,
          partyId: partyId,
          roleTypeId: pos.roleTypeId || recipient?.roleTypeId || (selectedSigner && selectedSigner.roleTypeId) || null,
          signatureTypeEnumId: pos.signatureTypeEnumId,
          pageNumber: pageIdx + 1,
          pageWidth: pageWidth,
          pageHeight: pageHeight,
          widthPx: widthPx,
          heightPx: heightPx,
          ulx: x,
          uly: y,
          fontSize: fontSize,
          partyName: recipient?.partyName || pos.partyName || partyInfo.partyName || '',
          identityCard: recipient?.identityCard || partyInfo.idValue || partyInfo.identityCard || '',
          emailAddress: recipient?.emailAddress || partyInfo.emailAddress || '',
          taxCode: recipient?.taxCode || partyInfo.partyTaxId || partyInfo.taxCode || '',
          contactNumber: recipient?.contactNumber || partyInfo.contactNumber || '',
          representativeName: recipient?.representativeName || partyInfo.representativeName || ''
        };
      });

      const signatureData = {
        agreementName: formData.agreementName,
        agreementNo: formData.agreementNo || undefined,
        agreementNoInt: formData.agreementNoInt || undefined,
        agreementNoFormatEnumId: formData.agreementNoFormatEnumId || undefined,
        agreementConfigureEnumId: formData.agreementType,
        description: formData.description || undefined,
        fromDate: formatDateForAPI(formData.effectiveDate as Date | null),
        thruDate: formatDateForAPI(formData.expiryDate as Date | null),
        isImportant: formData.isImportant ? 'Y' : 'N',
        isUrgent: formData.isUrgent ? 'Y' : 'N',
        mainFiles: mainFilesArray,
        attachmentFiles: attachmentFilesArray,
        agreementParties: agreementPartiesArray,
        signaturePositions: positions
      };
      const res = await AgreementService.saveSignaturePositions(agreementId as string, signatureData);
      if (res && res.success === 'Y') {
        ToastService.success('Tài liệu đã được tạo thành công!');
        setShowSignaturePlacement(false);
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem('econtract.taiLieuKy.activeTab', '0');
          }
        } catch (err) { }
        navigate('/quan-ly-tai-lieu/danh-sach-tai-lieu');
      } else {
        ToastService.error(res?.errorMessage || 'Không thể lưu vị trí ký');
      }
    } catch (e) {
      ToastService.error('Có lỗi xảy ra khi lưu vị trí ký');
    }
  };

  const generateDocumentCode = (agreementTypeOverride?: string | null) => {

    const agreementType = agreementTypeOverride ?? formData.agreementType;
    if (!agreementType) {
      ToastService.error('Vui lòng chọn Loại tài liệu trước');
      return;
    }
    // Lưu agreementType vào closure để đảm bảo giữ lại khi update formData
    const savedAgreementType = agreementType;
    AgreementService.generateDocumentCode(agreementType)
      .then(async (res: any) => {
        if (res && res.success === 'Y') {
          // Cập nhật agreementNo và agreementNoInt cùng lúc, đảm bảo giữ lại agreementType
          setFormData(prev => ({
            ...prev,
            agreementType: prev.agreementType || savedAgreementType, // Giữ lại agreementType nếu đã có, nếu không thì dùng savedAgreementType
            agreementNo: res.agreementNo,
            agreementNoInt: typeof res.agreementNoInt === 'number' ? res.agreementNoInt : null,
            agreementNoFormatEnumId: res.agreementNoFormatEnumId || null
          }));

          if (res.templateAgreementId) {
            setTemplateAgreementId(res.templateAgreementId);
            // Không tự động bật isTemplateMode ở đây để tránh khóa dropdown Loại tài liệu khi hệ thống chỉ gợi ý mẫu
          } else {
            setTemplateAgreementId(null);
          }

          // Tự động load thông tin từ mẫu tài liệu
          if (res.templateAgreementId && isTemplateMode) {
            try {
              const detail = await AgreementTemplateService.get(res.templateAgreementId);
              if (detail) {
                // 1. Set main file
                const templateFileId = detail.fileId || detail.googleFileId;
                if (templateFileId) {
                  const fileName = detail.fileName || 'Tài liệu từ mẫu';
                  const contentType = detail.contentType || 'application/vnd.google-apps.document';
                  const extension = extractFileExtension(fileName, contentType);

                  setMainFiles([{
                    name: fileName,
                    googleFileId: templateFileId,
                    fileId: templateFileId,
                    id: templateFileId,
                    googleFileUrl: `https://docs.google.com/document/d/${templateFileId}/edit`,
                    type: contentType,
                    extension: extension,
                    isMainFile: true
                  }]);
                }

                // 2. Set attachment files
                const attFiles = detail.attachmentFiles || [];
                if (Array.isArray(attFiles) && attFiles.length > 0) {
                  setAttachedFiles(attFiles.map((att: any) => ({
                    name: att.fileName || 'Tệp đính kèm',
                    fileId: att.fileId,
                    id: att.fileId,
                    contentType: att.contentType,
                    sequenceNum: att.sequenceNum,
                    isMainFile: false
                  })));
                }

                // 3. Set recipients & roleOptions từ roles của mẫu
                const roles = detail.roles || [];
                const recipients = Array.isArray(roles) ? roles.map((role: any) => ({
                  _internalId: crypto.randomUUID(),
                  party: null,
                  partyId: '',
                  taxCode: '',
                  partyName: '',
                  emailAddress: '',
                  contactNumber: '',
                  representativeName: '',
                  identityCard: '',
                  roleTypeId: role.roleTypeId || ''
                })) : [];

                if (recipients.length > 0) {
                  setDocumentRecipients(recipients);

                  setRoleOptions(prev => {
                    const existing = new Set(prev.map((r: any) => r.value));
                    const newRoles = roles
                      .filter((r: any) => r.roleTypeId && !existing.has(r.roleTypeId))
                      .map((r: any) => ({ value: r.roleTypeId, label: r.description || r.roleTypeId }));
                    return [...prev, ...newRoles];
                  });
                }

                // 4. Set signature positions từ mẫu
                const positions = detail.signaturePositions || [];
                if (Array.isArray(positions) && positions.length > 0) {
                  const mapped = positions.map((pos: any, idx: number) => {
                    const role = (detail.roles || []).find((r: any) => r.roleTypeId === pos.roleTypeId);
                    const label = role?.description || pos.partyName || pos.roleTypeId || '';

                    // Tìm recipient tương ứng đã tạo ở trên để lấy _internalId
                    const recipient = recipients.find((r: any) => r.roleTypeId === pos.roleTypeId);

                    return {
                      locationSeqId: pos.locationSeqId || formatSeq(idx + 1),
                      internalRecipientId: recipient?._internalId,
                      x: Number(pos.ulx ?? pos.llx ?? pos.x ?? 0),
                      y: Number(pos.uly ?? pos.lly ?? pos.y ?? 0),
                      pageIndex: Number(pos.pageNumber ?? pos.pageIndex ?? 1) - 1,
                      widthPx: Number(pos.widthPx ?? 200),
                      heightPx: Number(pos.heightPx ?? (pos.signatureTypeEnumId === 'AgtDigitalVerified' ? 65 : 100)),
                      fontSize: Number(pos.fontSize ?? 12),
                      signatureTypeEnumId: pos.signatureTypeEnumId,
                      roleTypeId: pos.roleTypeId || '',
                      partyName: label,
                      label: label
                    };
                  });
                  setSignaturePositions(normalizeLocationSeq(mapped));
                }
              }
            } catch (err) {
              console.error('Error loading template data', err);
            }
          }
        } else {
          ToastService.error(res?.error || 'Không tạo được mã tài liệu');
        }
      })
      .catch(() => {
        ToastService.error('Không tạo được mã tài liệu');
      });
  };

  const handleSubmit = async () => {
    if (!isViewMode) {
      setValidationTriggered(true);
      if (!validateForm()) {
        return;
      }
      if (!validateRequiredAttachedFiles()) {
        return;
      }
    }
    setShowRecipients(true);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const getCurrentStep = () => {
    if (showSignaturePlacement) return 3;
    if (showRecipients) return 2;
    return 1;
  };

  const renderStepIndicators = (expand: boolean) => {
    const currentStep = getCurrentStep();
    const connectorClass = expand ? 'flex-1 border-top-1' : 'border-top-1';
    const connectorStyle = {
      width: expand ? undefined : '80px',
      maxWidth: '120px',
      height: '2px',
      margin: '0 8px'
    } as React.CSSProperties;

    return (
      <div className={`flex align-items-center justify-content-center ${expand ? 'flex-1' : ''}`} style={{ gap: '12px' }}>
        {/* Step 1 */}
        <div className="flex align-items-center" style={{ gap: '8px' }}>
          <div
            className={`flex align-items-center justify-content-center border-circle ${currentStep >= 1 ? 'bg-primary text-white' : 'border-2 text-400'}`}
            style={{
              width: '32px',
              height: '32px',
              fontSize: '14px',
              fontWeight: '600',
              minWidth: '32px',
              backgroundColor: currentStep >= 1 ? 'var(--primary-color)' : 'var(--surface-card)',
              borderColor: currentStep >= 1 ? 'var(--primary-color)' : 'var(--surface-border)',
              color: currentStep >= 1 ? 'var(--primary-color-text)' : 'var(--text-color-secondary)'
            }}
          >
            1
          </div>
          <span
            className={`text-sm font-medium`}
            style={{ whiteSpace: 'nowrap', color: currentStep >= 1 ? 'var(--text-color)' : 'var(--text-color-secondary)' }}
          >
            Tải lên tài liệu
          </span>
        </div>

        {/* Connector Line */}
        <div
          className={`${connectorClass} ${currentStep >= 2 ? 'border-blue-500' : 'border-gray-300'}`}
          style={connectorStyle}
        ></div>

        {/* Step 2 */}
        <div className="flex align-items-center" style={{ gap: '8px' }}>
          <div
            className={`flex align-items-center justify-content-center border-circle ${currentStep >= 2 ? 'bg-primary text-white' : 'border-2 text-400'}`}
            style={{
              width: '32px',
              height: '32px',
              fontSize: '14px',
              fontWeight: '600',
              minWidth: '32px',
              backgroundColor: currentStep >= 2 ? 'var(--primary-color)' : 'var(--surface-card)',
              borderColor: currentStep >= 2 ? 'var(--primary-color)' : 'var(--surface-border)',
              color: currentStep >= 2 ? 'var(--primary-color-text)' : 'var(--text-color-secondary)'
            }}
          >
            2
          </div>
          <span
            className={`text-sm font-medium`}
            style={{ whiteSpace: 'nowrap', color: currentStep >= 2 ? 'var(--text-color)' : 'var(--text-color-secondary)' }}
          >
            Thiết lập người nhận
          </span>
        </div>

        {/* Connector Line */}
        <div
          className={`${connectorClass} ${currentStep >= 3 ? 'border-blue-500' : 'border-gray-300'}`}
          style={connectorStyle}
        ></div>

        {/* Step 3 */}
        <div className="flex align-items-center" style={{ gap: '8px' }}>
          <div
            className={`flex align-items-center justify-content-center border-circle ${currentStep >= 3 ? 'bg-primary text-white' : 'border-2 text-400'}`}
            style={{
              width: '32px',
              height: '32px',
              fontSize: '14px',
              fontWeight: '600',
              minWidth: '32px',
              backgroundColor: currentStep >= 3 ? 'var(--primary-color)' : 'var(--surface-card)',
              borderColor: currentStep >= 3 ? 'var(--primary-color)' : 'var(--surface-border)',
              color: currentStep >= 3 ? 'var(--primary-color-text)' : 'var(--text-color-secondary)'
            }}
          >
            3
          </div>
          <span
            className={`text-sm font-medium`}
            style={{ whiteSpace: 'nowrap', color: currentStep >= 3 ? 'var(--text-color)' : 'var(--text-color-secondary)' }}
          >
            Thiết lập vị trí ký
          </span>
        </div>
      </div>
    );
  };

  // Component render progress steps (có thể có hoặc không có nút)
  const renderProgressSteps = (showButtons: boolean = true, overrides?: { onNext?: () => void; onBack?: () => void; nextLabel?: string; backLabel?: string; showNext?: boolean; showBack?: boolean }) => {
    const showNext = overrides?.showNext ?? true;
    const showBack = overrides?.showBack ?? true;

    return (
      <div
        className={`px-4 py-3 border-bottom-1`}
        style={{
          backgroundColor: 'var(--surface-card)',
          borderColor: 'var(--surface-border)',
          zIndex: 10
        }}
      >
        <div
          className={`flex align-items-center ${showButtons ? 'justify-content-between' : 'justify-content-center'}`}
          style={{ gap: '12px' }}
        >
          {showButtons && showBack && (
            <Button
              label={overrides?.backLabel || "Quay lại"}
              icon="pi pi-arrow-left"
              className="p-button-outlined p-button-secondary"
              onClick={overrides?.onBack || handleCancel}
            />
          )}

          {renderStepIndicators(showButtons)}

          {showButtons && showNext && (
            <Button
              label={overrides?.nextLabel || "Tiếp tục"}
              icon="pi pi-arrow-right"
              onClick={overrides?.onNext || handleSubmit}
              className="p-button-success"
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--surface-ground)' }}>
      {initializing && <div className="p-4" style={{ color: 'var(--text-color)' }}>Đang tải...</div>}


      {/* Progress Steps với nút Quay lại và Tiếp tục - Chỉ hiển thị khi không có dialog nào mở */}
      {!showRecipients && !showSignaturePlacement && renderProgressSteps(true)}

      {/* Main Content */}
      <div className="p-3">
        <div className="mx-auto" style={{ maxWidth: '1600px' }}>
          <div className="grid">
            {/* Left Column - Document Information */}
            <div className="col-12 lg:col-4 xl:col-3">
              <Card title={<span style={{ color: 'var(--text-color)', fontSize: '1.25rem', fontWeight: 'bold' }}>Thông tin tài liệu</span>} className="h-full shadow-1 border-round-xl">


                <Divider className="mb-2" />

                {/* Document Marking */}
                <div className="field mb-3">
                  <label className="block font-medium mb-2" style={{ color: 'var(--text-color)' }}>Đánh dấu tài liệu</label>
                  <div className="flex gap-3">
                    <div className="flex align-items-center gap-2">
                      <Checkbox
                        inputId="important"
                        checked={formData.isImportant}
                        onChange={(e) => handleCheckboxChange('isImportant', !!e.checked)}
                        disabled={isViewMode}
                      />
                      <label htmlFor="important" className="text-sm">Quan trọng</label>
                    </div>
                    <div className="flex align-items-center gap-2">
                      <Checkbox
                        inputId="urgent"
                        checked={formData.isUrgent}
                        onChange={(e) => handleCheckboxChange('isUrgent', !!e.checked)}
                        disabled={isViewMode}
                      />
                      <label htmlFor="urgent" className="text-sm">Khẩn cấp</label>
                    </div>
                  </div>
                </div>

                {/* Document Name */}
                <div className="field mb-3">
                  <label htmlFor="agreementName" className="block font-medium mb-1" style={{ color: 'var(--text-color)' }}>
                    Tên tài liệu <span className="text-red-500">(*)</span>
                  </label>
                  {(() => {
                    const shouldShowError = !isViewMode && formData.agreementName.trim() === '' && (validationTriggered || touchedFields.agreementName);
                    return (
                      <>
                        <InputText
                          id="agreementName"
                          value={formData.agreementName}
                          onChange={(e) => handleInputChange('agreementName', e.target.value)}
                          className={shouldShowError ? 'p-invalid' : ''}
                          placeholder="Nhập tên tài liệu"
                          size="small"
                          disabled={isViewMode}
                        />
                        {shouldShowError && (
                          <small className="p-error text-xs">Không được để trống</small>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Document Type */}
                <div className="field mb-3">
                  <label htmlFor="agreementType" className="block font-medium mb-1" style={{ color: 'var(--text-color)' }}>
                    Loại tài liệu <span className="text-red-500">(*)</span>
                  </label>
                  <Dropdown
                    id="agreementType"
                    value={formData.agreementType}
                    options={documentTypes}
                    optionLabel="label"
                    optionValue="value"
                    filter
                    filterBy="label"
                    showClear
                    onChange={(e) => handleInputChange('agreementType', e.value)}
                    placeholder="Chọn loại tài liệu"
                    className={`w-full ${formData.agreementType === null ? 'p-invalid' : ''}`}
                    disabled={isViewMode}
                  />
                </div>
                {/* Date Fields */}
                <div className="field mb-3">
                  <div className="grid">
                    <div className="col-12 ">
                      <label htmlFor="effectiveDate" className="block font-medium mb-1" style={{ color: 'var(--text-color)' }}>Ngày hiệu lực</label>
                      <Calendar
                        ref={effectiveDateCalendarRef}
                        id="effectiveDate"
                        value={formData.effectiveDate}
                        onChange={(e) => handleInputChange('effectiveDate', e.value)}
                        placeholder="Chọn thời điểm"
                        showIcon
                        dateFormat="dd/mm/yy"
                        showTime
                        hourFormat="24"
                        showSeconds
                        panelClassName="time-right"
                        style={{ width: '100%' }}
                        maxDate={getEffectiveMaxDate()}
                        disabled={isViewMode}
                        footerTemplate={() => (
                          <div className="flex justify-content-end gap-2 p-2">
                            <Button
                              label="Xác nhận"
                              icon="pi pi-check"
                              onClick={() => {
                                if (effectiveDateCalendarRef.current) {
                                  effectiveDateCalendarRef.current.hide();
                                }
                              }}
                              className="p-button-sm"
                            />
                          </div>
                        )}
                      />
                    </div>
                    <div className="col-12 ">
                      <label htmlFor="expiryDate" className="block font-medium mb-1" style={{ color: 'var(--text-color)' }}>Ngày hết hiệu lực</label>
                      <Calendar
                        ref={expiryDateCalendarRef}
                        id="expiryDate"
                        value={formData.expiryDate}
                        onChange={(e) => handleInputChange('expiryDate', e.value)}
                        placeholder="Chọn thời điểm"
                        showIcon
                        dateFormat="dd/mm/yy"
                        minDate={getExpiryMinDate()}
                        readOnlyInput
                        showTime
                        hourFormat="24"
                        showSeconds
                        panelClassName="time-right"
                        style={{ width: '100%' }}
                        disabled={isViewMode}
                        footerTemplate={() => (
                          <div className="flex justify-content-end gap-2 p-2">
                            <Button
                              label="Xác nhận"
                              icon="pi pi-check"
                              onClick={() => {
                                if (expiryDateCalendarRef.current) {
                                  expiryDateCalendarRef.current.hide();
                                }
                              }}
                              className="p-button-sm"
                            />
                          </div>
                        )}
                      />
                      {!isViewMode && formData.expiryDate && (() => {
                        const now = new Date();
                        const expiry = new Date(formData.expiryDate as any);
                        const effective = formData.effectiveDate ? new Date(formData.effectiveDate as any) : null;
                        let minExpiryDate = new Date(now.getTime() + 60 * 60 * 1000); // now + 1 giờ
                        if (effective) {
                          const effectivePlusOneHour = new Date(effective.getTime() + 60 * 60 * 1000); // effectiveDate + 1 giờ
                          minExpiryDate = new Date(Math.max(minExpiryDate.getTime(), effectivePlusOneHour.getTime()));
                        }
                        const nowPlusOneHour = new Date(now.getTime() + 60 * 60 * 1000);
                        if (!effective && expiry.getTime() <= nowPlusOneHour.getTime()) {
                          return (
                            <small className="p-error text-xs">
                              Ngày hết hiệu lực phải lớn hơn thời điểm hiện tại ít nhất 1 giờ
                            </small>
                          );
                        }
                        const isInvalid = expiry <= minExpiryDate;
                        return isInvalid ? (
                          <small className="p-error text-xs">
                            Ngày hết hiệu lực phải lớn hơn ngày hiệu lực ít nhất 1 giờ
                          </small>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>

                {/* Document Code */}
                <div className="field mb-3">
                  <label htmlFor="agreementNo" className="block font-medium mb-1" style={{ color: 'var(--text-color)' }}>Mã tài liệu <span className="text-red-500">(*)</span></label>
                  <div className="p-inputgroup">
                    <InputText
                      id="documentCode"
                      value={formData.agreementNo}
                      onChange={(e) => handleInputChange('agreementNo', e.target.value)}
                      placeholder="Nhập mã tài liệu"
                      size="small"
                      disabled={isViewMode}
                    />
                    <Button
                      label="Tạo tự động"
                      className="p-button-outlined p-button-sm"
                      onClick={() => generateDocumentCode(formData.agreementType)}
                      size="small"
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="field mb-3">
                  <label htmlFor="description" className="block font-medium mb-1" style={{ color: 'var(--text-color)' }}>Mô tả</label>
                  <InputTextarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={10}
                    maxLength={500}
                    placeholder="Nhập mô tả tài liệu"
                    className="w-full"
                    disabled={isViewMode}
                  />
                  <small className="text-gray-600 text-xs mt-1">
                    {formData.description.length}/500
                  </small>
                </div>
              </Card>
            </div>

            {/* Right Column - File Management */}
            <div className="col-12 lg:col-8 xl:col-9">
              <Card
                title={<span style={{ color: 'var(--text-color)', fontSize: '1.25rem', fontWeight: 'bold' }}>TỆP TÀI LIỆU ({mainFiles.length})</span>}
                className="h-full shadow-1 border-round-xl"
              >
                <div className="grid mt-2">
                  {/* Upload Area */}
                  {!isViewMode && (
                    <div className="col-12 md:col-6 lg:col-4 xl:col-3 mb-3">
                      <div
                        className="border-2 border-dashed border-round p-4 text-center h-full transition-all hover:border-primary"
                        style={{
                          backgroundColor: 'var(--surface-variant)',
                          borderColor: 'var(--surface-border)',
                          height: '280px',
                          minHeight: '280px'
                        }}
                      >
                        <div className="flex flex-column align-items-center gap-3 h-full justify-content-center">
                          <i className="pi pi-upload text-4xl text-blue-500"></i>
                          <div>
                            <p className="text-gray-700 m-0 font-medium text-sm">Thêm vào tệp của bạn từ</p>
                          </div>
                          <div className="grid w-full">
                            <div className="col-6">
                              <div
                                className={`flex flex-column align-items-center gap-1 p-2 border-1 border-gray-300 border-round transition-all ${mainFiles.length > 0
                                  ? 'cursor-not-allowed opacity-50'
                                  : 'cursor-pointer hover:bg-white hover:shadow-2'
                                  }`}
                                onClick={mainFiles.length > 0 ? undefined : handleComputerUpload}
                              >
                                <i className="pi pi-folder text-xl text-blue-500"></i>
                                <span className="text-xs font-medium text-gray-700">Máy tính</span>
                              </div>
                            </div>
                            <div className="col-6">
                              <div
                                className={`flex flex-column align-items-center gap-1 p-2 border-1 border-gray-300 border-round transition-all ${mainFiles.length > 0
                                  ? 'cursor-not-allowed opacity-50'
                                  : 'cursor-pointer hover:bg-white hover:shadow-2'
                                  }`}
                                onClick={mainFiles.length > 0 ? undefined : handleCreateGoogleFile}
                              >
                                <i className="pi pi-file text-xl text-blue-500"></i>
                                <span className="text-xs font-medium text-gray-700">Soạn mới</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <small className="text-gray-500 text-xs">Định dạng được hỗ trợ: .pdf, .docx, .doc</small>
                          </div>
                          <Button
                            label="Thêm"
                            icon="pi pi-plus"
                            className="p-button-sm"
                            onClick={handleComputerUpload}
                            disabled={mainFiles.length > 0}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {mainFiles.map((file, index) => (
                    <div key={index} className="col-12 md:col-6 lg:col-4 xl:col-3 mb-3">
                      <div className="card p-3 shadow-1 border-round-lg" style={{ height: '350px', minHeight: '350px', backgroundColor: 'var(--surface-card)', border: '1px solid var(--surface-border)' }}>
                        <div className="flex flex-column h-full">
                          <div className="flex-1 mb-2 overflow-hidden" style={{ minHeight: 0 }}>
                            <div className="border-1 border-round p-2 text-center h-full flex align-items-center justify-content-center" style={{ backgroundColor: 'var(--surface-variant)', borderColor: 'var(--surface-border)' }}>
                              {(() => {
                                const fileKey = `${file.name}-${file.googleFileId}`;
                                const previewUrl = filePreviews[fileKey];
                                const isLoading = previewLoading[fileKey];

                                if (isLoading) {
                                  return (
                                    <div className="flex align-items-center justify-content-center h-full">
                                      <i className="pi pi-spin pi-spinner text-2xl text-blue-500"></i>
                                    </div>
                                  );
                                }

                                if (previewUrl) {
                                  // Chấp nhận render preview cho cả PDF gốc và Word đã convert
                                  const ext = (file.extension || '').toLowerCase();
                                  if (ext === 'pdf' || ext === 'docx' || ext === 'doc' || file.referenceFileId) {


                                    return (
                                      <img
                                        src={previewUrl}
                                        alt="First page preview"
                                        width="100%"
                                        height="100%"
                                        style={{ objectFit: 'contain', borderRadius: '4px' }}
                                      />
                                    );
                                  }
                                }

                                const ic = getIconByExtension(file.extension);
                                return (
                                  <div className="flex align-items-center justify-content-center h-full">
                                    <i className={`${ic.icon} ${ic.color} text-4xl`}></i>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>

                          <div className="flex-shrink-0 flex align-items-center justify-content-between gap-1 overflow-hidden" style={{ height: '32px' }}>
                            <div className="flex align-items-center gap-2 flex-1 overflow-hidden">
                              {(() => {
                                const ic = getIconByExtension(file.extension);
                                return <i className={`${ic.icon} ${ic.color} flex-shrink-0 text-sm`}></i>;
                              })()}
                              <span
                                className="font-medium text-sm"
                                title={file.name}
                                style={{ 
                                  color: 'var(--text-color)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  flex: 1
                                }}
                              >
                                {file.name}
                              </span>
                            </div>

                            <Menu
                              model={getFileMenuItems(file)}
                              popup
                              ref={(el) => {
                                if (el) {
                                  (file as any).menuRef = el;
                                }
                              }}
                            />
                            <Button
                              icon="pi pi-ellipsis-v"
                              className="p-button-text p-button-sm"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                (file as any).menuRef?.toggle(e);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {formData.agreementType && (
                  <div className="mt-4">
                    <div className="flex justify-content-between align-items-center mb-3">
                      <h6 className="m-0 font-medium" style={{ color: 'var(--text-color)' }}>TỆP ĐÍNH KÈM ({attachedFiles.length})</h6>
                    </div>
                    {documentTypeItemsLoading ? (
                      <div className="flex align-items-center gap-2 mt-1 text-sm text-primary">
                        <i className="pi pi-spin pi-spinner" />
                        <span>Đang tải cấu hình...</span>
                      </div>
                    ) : combinedTypeItems.length > 0 ? (
                      <div className="border-1 border-round p-0 mt-3 overflow-hidden shadow-1" style={{ borderColor: 'var(--surface-border)' }}>
                        <table className="w-full text-sm border-collapse" style={{ tableLayout: 'fixed', minWidth: '940px' }}>
                          <thead>
                            <tr className="text-left" style={{ backgroundColor: 'var(--surface-variant)' }}>
                              <th style={{ width: '50px', color: 'var(--text-color)' }} className="p-3 text-center border-bottom-1 surface-border">#</th>
                              <th style={{ width: '260px', color: 'var(--text-color)' }} className="p-3 border-bottom-1 surface-border">Mô tả tài liệu</th>
                              <th style={{ width: '320px', color: 'var(--text-color)' }} className="p-3 border-bottom-1 surface-border">Tệp tài liệu</th>
                              <th style={{ width: '165px', color: 'var(--text-color)' }} className="p-3 text-center border-bottom-1 surface-border">Hình thức</th>
                              <th style={{ width: '190px', color: 'var(--text-color)' }} className="p-3 text-center border-bottom-1 surface-border">Ngày nhận</th>
                            </tr>
                          </thead>
                          <tbody>
                            {combinedTypeItems.map((item: any, idx: number) => {
                              const seq = resolveConfigItemSeq(item, idx);
                              const seqKey = seq ? String(seq) : `idx-${idx}`;
                              const isRequired =
                                (() => {
                                  const flag = item?.isRequire ?? item?.isRequired ?? item?.required;
                                  return flag === 'Y' || flag === true;
                                })();
                              const meta = documentTypeItemMeta[seqKey] || {};
                              const file = attachedFiles.find((f: any) => {
                                const fileSeq = f?.sequenceNum ?? f?.configSequenceNum;
                                return Number(fileSeq) === Number(seq);
                              });

                              const currentFormType = meta.formType ?? (file?.agreementFormTypeEnumId ? normalizeFormTypeValue(file.agreementFormTypeEnumId) : null);
                              const currentReceivedDate = meta.receivedDate ?? (file?.agreementFormDate ? new Date(file.agreementFormDate) : null);

                              const isFormTypeInvalid = validationTriggered && (isRequired || file) && !currentFormType;
                              const isReceivedDateInvalid = validationTriggered && currentFormType === 'ATTACH_SCAN' && !currentReceivedDate;

                              return (
                                <tr key={seqKey} className="border-bottom-1" style={{ borderColor: 'var(--surface-border)' }}>
                                  <td className="p-3 text-center font-semibold" style={{ width: '50px', color: 'var(--text-color)' }}>
                                    {seq ?? idx + 1}
                                  </td>
                                  <td className="p-3" style={{ width: '260px', color: 'var(--text-color)' }}>
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        maxWidth: '100%',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }}
                                      title={getFileTypeName(item)}
                                    >
                                      {getFileTypeName(item)}
                                      {isRequired && <span className="text-red-500 ml-1">*</span>}
                                    </span>
                                  </td>
                                  <td className="p-3" style={{ width: '320px' }}>
                                    <div
                                      className="flex align-items-center gap-2"
                                      style={{ flexWrap: 'nowrap', width: '100%' }}
                                    >
                                      {!isViewMode && !file && (
                                        <Button
                                          type="button"
                                          label="Chọn tệp"
                                          icon="pi pi-folder"
                                          className="p-button-sm p-button-outlined"
                                          style={{ flexShrink: 0, minWidth: '100px' }}
                                          onClick={() => handleDocumentTypeItemUpload(item, idx)}
                                        />
                                      )}
                                      <span
                                        style={{
                                          display: 'inline-block',
                                          flex: '1 1 auto',
                                          minWidth: 0,
                                          maxWidth: '320px',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          color: file ? 'var(--text-color)' : 'var(--text-color-secondary)'
                                        }}
                                        title={file ? file.name : 'Không có tệp nào được chọn'}
                                      >
                                        {file ? file.name : 'Không có tệp nào được chọn'}
                                      </span>
                                      {file && !(file.uploading || pdfGenerating.has(file.fileId || file.googleFileId)) && (
                                        <div className="flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                                          <Button
                                            type="button"
                                            icon="pi pi-eye"
                                            className="p-button-rounded p-button-text p-button-sm"
                                            tooltip="Xem"
                                            style={{ width: '32px', height: '32px', padding: 0 }}
                                            onClick={() => handleViewFile(file)}
                                          />
                                          {!isViewMode  && (
                                            <Button
                                              type="button"
                                              icon="pi pi-trash"
                                              className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                              tooltip="Xóa"
                                              style={{ width: '32px', height: '32px', padding: 0 }}
                                              onClick={() => handleRemoveLocalFile(file)}
                                            />
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-3 text-center" style={{ width: '165px' }}>
                                    <Dropdown
                                      value={currentFormType}
                                      options={formTypeOptions}
                                      placeholder="Chọn"
                                      className={`w-full p-inputtext-sm ${isFormTypeInvalid ? 'p-invalid' : ''}`}
                                      onChange={(e) => handleDocumentTypeItemMetaChange(seqKey, 'formType', e.value)}
                                      disabled={isViewMode}
                                    />
                                  </td>
                                  <td className="p-3 text-center" style={{ width: '190px' }}>
                                    <Calendar
                                      value={currentReceivedDate}
                                      onChange={(e) => handleDocumentTypeItemMetaChange(seqKey, 'receivedDate', e.value)}
                                      showIcon
                                      dateFormat="dd/mm/yy"
                                      placeholder="Chọn ngày"
                                      className={`w-full p-inputtext-sm ${isReceivedDateInvalid ? 'p-invalid' : ''}`}
                                      disabled={isViewMode}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="mt-1 text-sm text-gray-500">
                        Loại tài liệu này chưa có cấu hình file đính kèm.
                      </div>
                    )}
                  </div>
                )}

                <FileUpload
                  ref={fileUploadRef}
                  mode="basic"
                  name="files"
                  accept=".pdf,.docx,.doc"
                  maxFileSize={10000000}
                  customUpload={true}
                  uploadHandler={handleFileUpload}
                  chooseLabel="Chọn tệp"
                  className="hidden"
                />
              </Card>
            </div>
          </div>
        </div>
      </div>
      {/* Recipients Dialog */}
      <Dialog
        header={
          <div className="w-full">
            {renderProgressSteps(true, {
              onBack: () => setShowRecipients(false),
              onNext: () => {
                if (validateRecipients()) {
                  setShowRecipients(false);
                  setShowSignaturePlacement(true);
                }
              }
            })}
          </div>
        }
        visible={showRecipients}
        style={{ width: '95vw', height: '95vh', maxWidth: 'none' }}
        modal
        headerClassName="surface-card text-color"
        contentClassName="surface-ground p-0"
        onHide={() => setShowRecipients(false)}
      >
        <div className="p-4">
          <div className="grid mt-3">
            <div className="col-12 flex justify-content-between flex-wrap">
              <b style={{ fontSize: '20px', color: 'var(--text-color)' }}>Người nhận</b>
            </div>
            <div className="col-12">
              {documentRecipients.map((rec, idx) => (
                <div className="col-12 card shadow-1 border-round-lg mb-3" style={{ padding: '1rem', backgroundColor: 'var(--surface-card)', border: '1px solid var(--surface-border)' }} key={rec._internalId || idx}>
                  <div className="flex col-12">
                    <div className="col-3 flex flex-column">
                      <label htmlFor="partyId"><b style={{ color: 'var(--text-color)' }}>Đối tượng</b> <span style={{ color: 'red' }}>*</span></label>
                      <div className="p-inputgroup mt-2" style={{ height: '34px' }}>
                        <AutoComplete
                          dropdown
                          field="label"
                          suggestions={getCustomerSuggestionsForRow(idx)}
                          value={rec.party}
                          onChange={(e) => handleRecipientChange(idx, 'party', e.value)}
                          completeMethod={searchCustomers}
                          placeholder="Tìm kiếm đối tượng"
                          disabled={isViewMode}
                        />
                        {!isViewMode && (
                          <span className="p-inputgroup-addon" style={{ background: '#22C55E', color: 'white', cursor: 'pointer' }} onClick={() => createCustomer(idx)}>
                            <i className="pi pi-plus" />
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-column gap-2 col-3">
                      <label htmlFor="taxCode"><b style={{ color: 'var(--text-color)' }}>Mã số thuế</b></label>
                      <InputText
                        id="taxCode"
                        name="taxCode"
                        value={rec.taxCode || ''}
                        onChange={(e) => handleRecipientChange(idx, 'taxCode', e.target.value)}
                        onInput={(e) => {
                          const value = (e.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
                          if (value !== (e.target as HTMLInputElement).value) {
                            (e.target as HTMLInputElement).value = value;
                            handleRecipientChange(idx, 'taxCode', value);
                          }
                        }}
                        onPaste={(e) => {
                          const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                          if (!/^[0-9]*$/.test(paste)) {
                            e.preventDefault();
                          }
                        }}
                        maxLength={13}
                        pattern="[0-9]*"
                        inputMode="numeric"
                        disabled={isViewMode}
                      />
                    </div>
                    <div className="flex flex-column gap-2 col-3">
                      <label htmlFor="partyName"><b style={{ color: 'var(--text-color)' }}>Tên công ty</b></label>
                      <InputText
                        id="partyName"
                        name="partyName"
                        value={rec.partyName || ''}
                        onInput={(e) => {
                          const inputEl = e.target as HTMLInputElement;
                          const value = inputEl.value.replace(/[^a-zA-ZÀ-ỹ\s-]/g, '');
                          if (value !== inputEl.value) inputEl.value = value;
                        }}
                        onChange={(e) => handleRecipientChange(idx, 'partyName', e.target.value)}
                        pattern="[a-zA-ZÀ-ỹ\s-]+"
                        title="Chỉ được nhập chữ cái và khoảng trắng"
                        disabled={isViewMode}
                      />
                    </div>
                    <div className="flex flex-column gap-2 col-3">
                      <label htmlFor="emailAddress"><b style={{ color: 'var(--text-color)' }}>Email</b> <span style={{ color: 'red' }}>*</span></label>
                      <InputText
                        id="emailAddress"
                        name="emailAddress"
                        value={rec.emailAddress || ''}
                        onChange={(e) => handleRecipientChange(idx, 'emailAddress', e.target.value)}
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                  <div className="flex col-12">
                    <div className="flex flex-column gap-2 col-3">
                      <label htmlFor="contactNumber"><b style={{ color: 'var(--text-color)' }}>Số điện thoại</b></label>
                      <InputText
                        id="contactNumber"
                        name="contactNumber"
                        value={rec.contactNumber || ''}
                        onInput={(e) => {
                          const value = (e.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
                          (e.target as HTMLInputElement).value = value;
                          handleRecipientChange(idx, 'contactNumber', value);
                        }}
                        onPaste={(e) => {
                          const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                          if (!/^[0-9]*$/.test(paste)) {
                            e.preventDefault();
                          }
                        }}
                        maxLength={10}
                        pattern="[0-9]*"
                        inputMode="numeric"
                        disabled={isViewMode}
                      />
                    </div>
                    <div className="flex flex-column gap-2 col-3">
                      <label htmlFor="representativeName"><b style={{ color: 'var(--text-color)' }}>Họ và Tên</b></label>
                      <InputText
                        id="representativeName"
                        name="representativeName"
                        value={rec.representativeName || ''}
                        onInput={(e) => {
                          const inputEl = e.target as HTMLInputElement;
                          const value = inputEl.value.replace(/[^a-zA-ZÀ-ỹ\s-]/g, '');
                          if (value !== inputEl.value) inputEl.value = value;
                        }}
                        onChange={(e) => handleRecipientChange(idx, 'representativeName', e.target.value)}
                        onPaste={(e) => {
                          const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                          if (/[^a-zA-ZÀ-ỹ\s-]/.test(paste)) {
                            e.preventDefault();
                          }
                        }}
                        pattern="[a-zA-ZÀ-ỹ\s-]+"
                        title="Chỉ được nhập chữ cái và khoảng trắng"
                        disabled={isViewMode || rec.partyTypeEnumId === 'PtyPerson' || rec.party?.partyTypeEnumId === 'PtyPerson'}
                      />
                    </div>
                    <div className="flex flex-column gap-2 col-3">
                      <label htmlFor="identityCard"><b style={{ color: 'var(--text-color)' }}>CCCD/CMND</b></label>
                      <InputText
                        id="identityCard"
                        name="identityCard"
                        value={rec.identityCard || ''}
                        onInput={(e) => {
                          const value = (e.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
                          const limitedValue = value.slice(0, 12);
                          (e.target as HTMLInputElement).value = limitedValue;
                          handleRecipientChange(idx, 'identityCard', limitedValue);
                        }}
                        onPaste={(e) => {
                          const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                          if (!/^[0-9]*$/.test(paste)) {
                            e.preventDefault();
                          }
                        }}
                        maxLength={12}
                        pattern="[0-9]*"
                        inputMode="numeric"
                        disabled={isViewMode || rec.partyTypeEnumId === 'PtyOrganization' || rec.party?.partyTypeEnumId === 'PtyOrganization'}
                      />
                    </div>
                    <div className="flex flex-column gap-2 col-3">
                      <label htmlFor="roleTypeId"><b>Vai trò</b> <span style={{ color: 'red' }}>*</span></label>
                      <Dropdown
                        id="roleTypeId"
                        name="roleTypeId"
                        options={getRoleOptionsForRow(idx)}
                        optionLabel="label"
                        optionValue="value"
                        value={rec.roleTypeId}
                        onChange={(e) => handleRecipientChange(idx, 'roleTypeId', e.value)}
                        disabled={isViewMode || isTemplateMode || isFromTemplateAgreement}
                      />
                    </div>
                    {/* Tạm ẩn thứ tự theo yêu cầu đại ca
                    {isSequential && (
                      <div className="flex flex-column gap-2 col-1">
                        <label htmlFor="signingOrder"><b>Thứ tự</b></label>
                        <InputText
                          id="signingOrder"
                          type="number"
                          value={rec.signingOrder || (idx + 1)}
                          onChange={(e) => handleRecipientChange(idx, 'signingOrder', parseInt(e.target.value) || 1)}
                          min={1}
                          disabled={isViewMode}
                        />
                      </div>
                    )} */}
                  </div>
                  {!isViewMode && !isFromTemplateAgreement && (
                    <Button
                      className="mt-1"
                      size="small"
                      onClick={() => removeRecipient(idx)}
                      icon="pi pi-trash"
                      label="Xóa"
                      severity="danger"
                      disabled={documentRecipients.length === 1}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="col-12 flex">
              {!isViewMode && !isFromTemplateAgreement && (
                <Button
                  icon="pi pi-plus"
                  size="small"
                  label="Thêm người nhận"
                  onClick={addRecipient}
                  disabled={documentRecipients.length >= roleOptions.length}
                />
              )}
            </div>
          </div>
        </div>
      </Dialog>
      <Dialog
        header={
          <div className="w-full">
            {renderProgressSteps(true, {
              nextLabel: 'Hoàn thành',
              showNext: !isViewMode,
              onBack: () => {
                setShowSignaturePlacement(false);
                setShowRecipients(true);
              },
              onNext: handleFinalSubmit
            })}
          </div>
        }
        visible={showSignaturePlacement}
        style={{ width: '95vw', height: '95vh', maxWidth: 'none' }}
        modal
        headerClassName="surface-card text-color"
        contentClassName="surface-ground p-0"
        onHide={() => setShowSignaturePlacement(false)}
      >
        <Step3Positions
          documentRecipients={documentRecipients}
          signaturePositions={signaturePositions}
          selectedSigner={selectedSigner}
          setSelectedSigner={setSelectedSigner}
          canvasLoading={canvasLoading}
          pageCanvases={pageCanvases}
          canvasRefs={canvasRefs}
          draggableRefs={draggableRefs}
          handleDrop={handleDrop}
          handleDragStop={handleDragStop}
          removeSignaturePosition={removeSignaturePosition}
          setSignaturePositions={setSignaturePositions}
          normalizeLocationSeq={normalizeLocationSeq}
          isTemplateMode={isTemplateMode || isFromTemplateAgreement}
          selectedSignatureType={selectedSignatureType}
          setSelectedSignatureType={setSelectedSignatureType}
          mainFiles={mainFiles}
          activeMainFileIndex={activeMainFileIndex}
          onSetActiveFile={setActiveMainFileIndex}
        />
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog
        header={
          <div className="flex justify-content-between align-items-center w-full">
            <span className="font-semibold text-lg">{selectedFile?.name || selectedFile?.fileName || 'Xem tài liệu'}</span>
          </div>
        }
        visible={showFileViewer}
        style={{ width: '98vw', height: '95vh', maxWidth: 'none' }}
        contentClassName="p-0 flex flex-column"
        onHide={() => {
          setShowFileViewer(false);
          setViewerUrl(null);
          setSelectedFile(null);
        }}
        footer={
          <div className="flex justify-content-end">
            <Button
              icon="pi pi-times"
              label="Đóng"
              className="p-button-outlined p-button-secondary p-button-sm"
              onClick={() => setShowFileViewer(false)}
            />
          </div>
        }
      >
        {viewerUrl && (
          <PdfCanvasViewer
            fileId={viewerUrl}
            fileName={selectedFile?.name || selectedFile?.fileName || 'document.pdf'}
            scale={2.0}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}
      </Dialog>

      <EditorDialog
        display={displayEditor}
        setDisplay={setDisplayEditor}
        agreementId={agreementId}
        showRecipients={showRecipients}
        setShowRecipients={setShowRecipients}
        validateRecipients={validateRecipients}
        setMainFiles={setMainFiles}
        setAttachedFiles={setAttachedFiles}
      />
      {customerDialog.render()}
      {/* Leave Page Confirm Dialog */}
      <Dialog
        header="Xác nhận rời trang"
        visible={showLeavePrompt}
        style={{ width: '32rem' }}
        onHide={() => setShowLeavePrompt(false)}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button
              label="Ở lại trang"
              icon="pi pi-times"
              className="p-button-outlined p-button-secondary p-button-sm"
              onClick={() => setShowLeavePrompt(false)}
            />
            <Button
              label="Rời trang"
              icon="pi pi-check"
              className="p-button-danger p-button-sm"
              onClick={() => {
                setShowLeavePrompt(false);
                setHasUnsavedChanges(false);
                if (pendingHash) {
                  window.location.hash = pendingHash;
                }
              }}
            />
          </div>
        )}
      >
        <div className="p-2">
          Bạn có thay đổi chưa lưu. Rời trang sẽ mất các thay đổi. Tiếp tục?
        </div>
      </Dialog>
      {fileOperationLoading && (
        <div
          className="fixed top-0 left-0 w-full h-full flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 1500 }}
        >
          <div className="surface-card p-4 border-round flex align-items-center gap-3 shadow-5">
            <i className="pi pi-spin pi-spinner text-2xl text-primary"></i>
            <span className="text-lg font-semibold">Đang xử lý tệp, vui lòng đợi...</span>
          </div>
        </div>
      )}
      {customerDialog.render()}
    </div>
  );
};
const EditorDialog = ({ display, setDisplay, agreementId, showRecipients, setShowRecipients, validateRecipients, setMainFiles, setAttachedFiles }: any) => {
  const [fileId, setFileId] = useState<any>(false);
  const [originalFileId, setOriginalFileId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');

  React.useEffect(() => {
    if (display?.fileId) {
      setFileId(display.fileId);
      setOriginalFileId(display.originalFileId || null);
      if (display.fileName) {
        setFileName(display.fileName);
      }
    }
  }, [display]);

  const handleSaveFile = async () => {
    console.log('>>> handleSaveFile (Hybrid Storage):', { fileId, originalFileId, agreementId, fileName });
    if (!fileId) return;
    if (!fileName || !fileName.trim()) {
      ToastService.error('Vui lòng nhập tên tài liệu');
      return;
    }

    try {
      let res;
      if (originalFileId) {
        console.log('>>> Calling saveEditor (Sync GD to MinIO):', { agreementId, originalFileId, fileId });
        // Luồng Hybrid Storage: Đồng bộ từ GD (fileId) về MinIO (originalFileId)
        res = await AgreementService.saveEditor(agreementId, originalFileId, fileId);
      } else {
        console.log('>>> Calling syncEditorToMinio (GD -> MinIO):', { fileId, fileName });
        // Luồng mới: Đồng bộ từ GD về MinIO ngay khi tạo mới trên GD
        res = await AgreementService.syncEditorToMinio(fileId, fileName.trim(), display.contentType || 'application/vnd.google-apps.document');
        if (res && res.success === 'Y') {
          // Cập nhật lại originalFileId bằng ID từ MinIO để các lần lưu sau dùng saveEditor
          setOriginalFileId(res.fileId);
        }
      }
      console.log('>>> Save result:', res);

      if (res && res.success === 'Y') {
        const finalId = res.fileId || originalFileId || fileId;
        const mappedMain = ({
          name: fileName.trim(),
          size: 0,
          type: extractFileExtension(fileName.trim(), display.contentType || 'docx'),
          file: undefined,
          extension: 'docx',
          googleFileId: finalId,
          fileId: finalId,
          id: finalId,
          googleFileUrl: res.contentLocation || `https://docs.google.com/document/d/${fileId}/edit`,
          contentLocation: res.contentLocation,
          referenceFileId: res.referenceFileId || undefined,
          isMainFile: true
        });
        setMainFiles(prev => [...prev, mappedMain]);
        setTimeout(() => setDisplay(false), 0);
        ToastService.success(`Tài liệu đã được lưu thành công!`);
      } else {
        ToastService.error(res?.error || 'Không thể lưu tài liệu');
      }
    } catch (e: any) {
      ToastService.error('Error saving file');
    }
  }
  return (
    <Dialog
      header="Editor"
      visible={!!display}
      style={{ width: '98vw' }}
      position="top"
      onHide={() => setDisplay(false)}
      footer={(
        <div className="flex justify-content-end gap-2">
          <span className="p-inputgroup" style={{ maxWidth: 360 }}>
            <span className="p-inputgroup-addon"><i className="pi pi-file" /></span>
            <InputText value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="Nhập tên tài liệu" />
          </span>
          <Button label="Lưu tài liệu" icon="pi pi-save" type="button" severity="success" size="small" onClick={() => handleSaveFile()} />
          <Button label="Đóng" icon="pi pi-times" type="button" outlined severity="secondary" size="small" onClick={() => setDisplay(false)} />
        </div>
      )}
    >
      {fileId && (
        <iframe
          src={`https://docs.google.com/document/d/${fileId}/edit`}
          width="100%"
          height="800"
          style={{ border: 'none' }}
          referrerPolicy="no-referrer"
        ></iframe>
      )}
    </Dialog>
  );
};
